import { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Package, Ruler, Shield, Layers, X, Grid3X3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet";
import ExploreMore from "@/components/ExploreMore";
import LazyImage from "@/components/LazyImage";
import RelatedReading from "@/components/RelatedReading";
import useCanonical from "@/hooks/useCanonical";
import useMetaDescription from "@/hooks/useMetaDescription";
import usePageSEOOverride from "@/hooks/useSEOOverrides";
import useSlugAlias from "@/hooks/useSlugAlias";
import { GEO_SEO_OVERRIDES, GEO_FAQS, buildProductSchema, buildFaqSchema, buildSpeakableSchema } from "@/data/geoSeo";

// Complete product database with specifications
const PRODUCTS_DATA = {
  // IT & Technology Products
  "linear-workstations": {
    name: "Linear Workstations",
    slug: "linear-workstations",
    category: "Workstations",
    shortDescription: "Efficient linear workstation systems designed for IT offices and tech teams.",
    fullDescription: "Our Linear Workstations are designed for maximum space efficiency in IT and technology offices. These modular systems allow easy reconfiguration as teams grow, featuring integrated cable management and clean aesthetics that support a productive tech environment.",
    specifications: [
      { label: "Dimensions", value: "1200mm x 600mm x 750mm (Standard)" },
      { label: "Material", value: "Pre-laminated Particle Board (PLPB) with powder-coated steel frame" },
      { label: "Table Top Thickness", value: "25mm" },
      { label: "Leg Frame", value: "50mm x 25mm CRCA steel, powder coated" },
      { label: "Cable Management", value: "Integrated wire tray and desk-mounted cable ports" },
      { label: "Panel Height Options", value: "300mm, 450mm, or full-height partitions" },
      { label: "Weight Capacity", value: "80 kg uniformly distributed load" },
      { label: "Certification", value: "ISO 9001:2015 certified" },
    ],
    features: [
      "Modular design for easy reconfiguration",
      "Integrated cable management system",
      "Multiple panel height options",
      "Soft-close drawer mechanisms",
      "Scratch-resistant laminate finish",
      "Adjustable leveling feet",
    ],
    industries: ["IT & Technology", "BPO", "Co-working", "Corporate HQ"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/2ipzm9lu_45.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/2ipzm9lu_45.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/eh25qfmh_32.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/7u8rtj3v_33.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/3a2srzfu_34.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/scf25ofp_36.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/egjovpf9_42.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/96qm1lxd_38.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/onure7l0_39.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/iep5x7df_40.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/90r5hj54_41.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/av3iry66_49.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/j161qi6q_43.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/o1dto4pw_37.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/zwqdz32v_47.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/hhzcebbj_48.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/0yp4tlil_53.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/cgthdpfq_54.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/5juzkrq3_50.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/3cv9q5f0_51.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/eq2kghl2_52.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/lej97ewg_59.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ryb5r3qu_55.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/zgyyay44_56.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/0u7vv4yn_57.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/b797slxl_58.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/2k0yfrfa_Gemini_Generated_Image_3y5npy3y5npy3y5n.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/wpxh3vm0_60.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ec8x00y3_61.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/mqk2v8fv_62.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ol9tkpbo_63.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/wvoa6gnk_imgi_3_Express_4_way.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/b4azuv30_Gemini_Generated_Image_6t5z0a6t5z0a6t5z.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ll56ynai_Gemini_Generated_Image_yjx9qnyjx9qnyjx9.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/uo66j50e_imgi_3_Core_O_Leg_38x38.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/tfsp7ej0_imgi_3_Core-High-Table-Updated-WEB-SIZE.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/bhw9yrju_Screenshot%202026-03-26%20130224.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/kph9td39_imgi_6_express.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/jj85jww5_imgi_39_4-Way-Eurospace.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/eryduga6_Screenshot%202026-03-26%20124306.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/jxb7769c_Screenshot%202026-03-26%20125716.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/8vqoiqzh_Screenshot%202026-03-26%20133657.png",
      "/media/linear-workstations/linear-workstation-042.jpg",
      "/media/linear-workstations/linear-workstation-043.jpg",
      "/media/linear-workstations/linear-workstation-044.jpg",
      "/media/linear-workstations/linear-workstation-045.jpg",
      "/media/linear-workstations/linear-workstation-046.jpg",
      "/media/linear-workstations/linear-workstation-047.jpg",
      "/media/linear-workstations/linear-workstation-048.jpg",
      "/media/linear-workstations/linear-workstation-049.jpg",
      "/media/linear-workstations/linear-workstation-050.jpg",
      "/media/linear-workstations/linear-workstation-051.jpg",
      "/media/linear-workstations/linear-workstation-052.jpg",
      "/media/linear-workstations/linear-workstation-053.jpg",
      "/media/linear-workstations/linear-workstation-054.jpg",
      "/media/linear-workstations/linear-workstation-055.jpg",
      "/media/linear-workstations/linear-workstation-056.jpg",
      "/media/linear-workstations/linear-workstation-057.jpg",
      "/media/linear-workstations/linear-workstation-058.jpg",
      "/media/linear-workstations/linear-workstation-059.jpg",
      "/media/linear-workstations/linear-workstation-060.jpg",
      "/media/linear-workstations/linear-workstation-061.jpg",
      "/media/linear-workstations/linear-workstation-062.jpg",
      "/media/linear-workstations/linear-workstation-063.jpg",
      "/media/linear-workstations/linear-workstation-064.jpg",
      "/media/linear-workstations/linear-workstation-065.jpg",
      "/media/linear-workstations/linear-workstation-066.jpg",
      "/media/linear-workstations/linear-workstation-067.jpg",
      "/media/linear-workstations/linear-workstation-068.jpg",
      "/media/linear-workstations/linear-workstation-069.jpg",
      "/media/linear-workstations/linear-workstation-070.jpg",
      "/media/linear-workstations/linear-workstation-071.jpg",
      "/media/linear-workstations/linear-workstation-072.jpg",
      "/media/linear-workstations/linear-workstation-073.jpg",
      "/media/linear-workstations/linear-workstation-074.jpg",
      "/media/linear-workstations/linear-workstation-075.jpg",
      "/media/linear-workstations/linear-workstation-076.jpg",
      "/media/linear-workstations/linear-workstation-077.jpg",
      "/media/linear-workstations/linear-workstation-078.jpg",
      "/media/linear-workstations/linear-workstation-079.jpg",
      "/media/linear-workstations/linear-workstation-080.jpg",
      "/media/linear-workstations/linear-workstation-081.jpg",
      "/media/linear-workstations/linear-workstation-082.jpg",
      "/media/linear-workstations/linear-workstation-083.jpg",
      "/media/linear-workstations/linear-workstation-084.jpg",
      "/media/linear-workstations/linear-workstation-085.jpg",
      "/media/linear-workstations/linear-workstation-086.jpg",
      "/media/linear-workstations/linear-workstation-087.jpg",
      "/media/linear-workstations/linear-workstation-088.jpg",
      "/media/linear-workstations/linear-workstation-089.jpg",
      "/media/linear-workstations/linear-workstation-090.jpg",
      "/media/linear-workstations/linear-workstation-091.jpg",
      "/media/linear-workstations/linear-workstation-093.jpg",
      "/media/linear-workstations/linear-workstation-094.jpg",
      "/media/linear-workstations/linear-workstation-095.jpg",
      "/media/linear-workstations/linear-workstation-096.jpg",
      "/media/linear-workstations/linear-workstation-097.jpg",
      "/media/linear-workstations/linear-workstation-098.jpg",
      "/media/linear-workstations/linear-workstation-099.jpg",
      "/media/linear-workstations/linear-workstation-100.jpg",
      "/media/linear-workstations/linear-workstation-101.jpg",
      "/media/linear-workstations/linear-workstation-102.jpg",
      "/media/linear-workstations/linear-workstation-103.jpg",
      "/media/linear-workstations/linear-workstation-104.jpg",
    ],
  },
  "corner-workstations": {
    name: "Modular Office Corner Workstation",
    slug: "corner-workstations",
    category: "Workstations",
    shortDescription: "L-shaped modular corner workstations that turn awkward floor corners into productive workspaces — extra surface area for dual monitors, documents and collaboration.",
    fullDescription: "Ezeefit's Modular Office Corner Workstation is an L-shaped desk system engineered for corner placement in open-plan and cabin layouts. Each corner workstation gives a single user roughly 40% more usable desktop surface than a linear desk of the same footprint — ideal for dual-monitor setups, finance & design teams, and executives who need spread-out workspace. Manufactured in-house at our Bhiwandi (Thane) factory in 4-hour batches, corner workstations ship in 4, 6, 8 and 12-seater cluster configurations with integrated cable management, powder-coated CRCA steel frames, 25 mm pre-laminated table-tops, and modular partition screens (fabric, glass or laminate). Full ISO 9001:2015 quality assurance, 1–3 day site installation across Mumbai, Navi Mumbai, Thane, Pune and pan-India delivery.",
    specifications: [
      { label: "Configuration", value: "L-shaped corner desk — single-user, 4-, 6-, 8- and 12-seater cluster options" },
      { label: "Dimensions (Single)", value: "1500 mm × 1500 mm × 750 mm (custom sizes available)" },
      { label: "Table-top Thickness", value: "25 mm pre-laminated particle board (PLPB)" },
      { label: "Leg Frame", value: "50 × 25 mm CRCA steel, powder-coated" },
      { label: "Cable Management", value: "Integrated wire tray, desk-mounted flip-top cable ports" },
      { label: "Partition Panel Options", value: "300, 450 or 900 mm — fabric, glass or laminate" },
      { label: "Storage", value: "Optional 2 / 3-drawer mobile pedestal with soft-close" },
      { label: "Weight Capacity", value: "100 kg uniformly distributed load" },
      { label: "Finish Options", value: "60+ laminate shades, 8 solid-core edge bands, 12 fabric colours" },
      { label: "Certification", value: "ISO 9001:2015 certified manufacture" },
      { label: "Installation Time", value: "1–3 days (Mumbai / Thane / Navi Mumbai)" },
    ],
    features: [
      "L-shaped geometry maximises usable desktop area in floor corners",
      "Dual-monitor + laptop-friendly surface layout",
      "Modular cluster reconfiguration — grow from 4 to 12 seats without replacing frames",
      "Integrated cable spine keeps power, LAN and USB out of sight",
      "Scratch-resistant textured laminate finishes",
      "Optional acoustic partition screens for focus-heavy roles",
      "Manufactured in-house at Bhiwandi factory — no outsourcing",
      "1-year replacement warranty on the frame, 3-year on the panel",
    ],
    industries: ["IT & Technology", "BFSI", "BPO / KPO", "Corporate HQ", "Design & Media", "Co-working", "Start-ups"],
    image: "/media/corner-workstations/corner-workstation-37.jpg",
    images: [
      "/media/corner-workstations/corner-workstation-01.jpg",
      "/media/corner-workstations/corner-workstation-02.jpg",
      "/media/corner-workstations/corner-workstation-03.jpg",
      "/media/corner-workstations/corner-workstation-04.jpg",
      "/media/corner-workstations/corner-workstation-05.jpg",
      "/media/corner-workstations/corner-workstation-06.jpg",
      "/media/corner-workstations/corner-workstation-07.jpg",
      "/media/corner-workstations/corner-workstation-08.jpg",
      "/media/corner-workstations/corner-workstation-09.jpg",
      "/media/corner-workstations/corner-workstation-10.jpg",
      "/media/corner-workstations/corner-workstation-11.jpg",
      "/media/corner-workstations/corner-workstation-12.jpg",
      "/media/corner-workstations/corner-workstation-13.jpg",
      "/media/corner-workstations/corner-workstation-14.jpg",
      "/media/corner-workstations/corner-workstation-15.jpg",
      "/media/corner-workstations/corner-workstation-16.jpg",
      "/media/corner-workstations/corner-workstation-17.jpg",
      "/media/corner-workstations/corner-workstation-18.jpg",
      "/media/corner-workstations/corner-workstation-19.jpg",
      "/media/corner-workstations/corner-workstation-20.jpg",
      "/media/corner-workstations/corner-workstation-21.jpg",
      "/media/corner-workstations/corner-workstation-22.jpg",
      "/media/corner-workstations/corner-workstation-23.jpg",
      "/media/corner-workstations/corner-workstation-24.jpg",
      "/media/corner-workstations/corner-workstation-25.jpg",
      "/media/corner-workstations/corner-workstation-26.jpg",
      "/media/corner-workstations/corner-workstation-27.jpg",
      "/media/corner-workstations/corner-workstation-28.jpg",
      "/media/corner-workstations/corner-workstation-29.jpg",
      "/media/corner-workstations/corner-workstation-30.jpg",
      "/media/corner-workstations/corner-workstation-31.jpg",
      "/media/corner-workstations/corner-workstation-32.jpg",
      "/media/corner-workstations/corner-workstation-33.jpg",
      "/media/corner-workstations/corner-workstation-34.jpg",
      "/media/corner-workstations/corner-workstation-35.jpg",
      "/media/corner-workstations/corner-workstation-36.jpg",
      "/media/corner-workstations/corner-workstation-37.jpg",
      "/media/corner-workstations/corner-workstation-38.jpg",
      "/media/corner-workstations/corner-workstation-39.jpg",
      "/media/corner-workstations/corner-workstation-40.jpg",
      "/media/corner-workstations/corner-workstation-41.jpg",
      "/media/corner-workstations/corner-workstation-42.jpg",
      "/media/corner-workstations/corner-workstation-43.jpg",
      "/media/corner-workstations/corner-workstation-44.jpg",
      "/media/corner-workstations/corner-workstation-45.jpg",
      "/media/corner-workstations/corner-workstation-46.jpg",
      "/media/corner-workstations/corner-workstation-47.jpg",
      "/media/corner-workstations/corner-workstation-48.jpg",
      "/media/corner-workstations/corner-workstation-49.jpg",
      "/media/corner-workstations/corner-workstation-50.jpg",
      "/media/corner-workstations/corner-workstation-51.jpg",
      "/media/corner-workstations/corner-workstation-52.jpg",
      "/media/corner-workstations/corner-workstation-53.jpg",
      "/media/corner-workstations/corner-workstation-54.jpg",
      "/media/corner-workstations/corner-workstation-55.jpg",
      "/media/corner-workstations/corner-workstation-56.jpg",
      "/media/corner-workstations/corner-workstation-57.jpg",
      "/media/corner-workstations/corner-workstation-58.jpg",
      "/media/corner-workstations/corner-workstation-59.jpg",
      "/media/corner-workstations/corner-workstation-60.jpg",
      "/media/corner-workstations/corner-workstation-61.jpg",
    ],
  },
  "height-adjustable-desks": {
    name: "Height Adjustable Desks",
    slug: "height-adjustable-desks",
    category: "Ergonomic Furniture",
    shortDescription: "Electric sit-stand desks giving teams the flexibility to switch postures during long working hours.",
    fullDescription: "Height Adjustable Desks let team members switch between sitting and standing positions throughout the workday. Our electric models feature smooth, quiet motors and programmable height presets for personalized comfort.",
    specifications: [
      { label: "Height Range", value: "650mm to 1300mm (electric adjustment)" },
      { label: "Table Top Size", value: "1400mm x 700mm (Standard), custom sizes available" },
      { label: "Motor Type", value: "Dual motor electric system" },
      { label: "Lifting Capacity", value: "120 kg" },
      { label: "Speed", value: "38mm/second" },
      { label: "Noise Level", value: "< 50 dB" },
      { label: "Memory Presets", value: "4 programmable height settings" },
      { label: "Anti-Collision", value: "Built-in safety sensor" },
    ],
    features: [
      "Electric dual-motor lift system",
      "4 programmable height memory presets",
      "Anti-collision safety technology",
      "LED control panel",
      "Cable management tray included",
      "5-year motor warranty",
    ],
    industries: ["IT & Technology", "Corporate HQ", "Start-ups"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/31q3tjl7_Screenshot%202026-03-24%20160527.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/31q3tjl7_Screenshot%202026-03-24%20160527.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/foxxaecm_46.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/oi6czfkz_65.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/z4dlj7cr_Screenshot%202026-03-14%20142405.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/x10582go_Screenshot%202026-03-24%20154157.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/9waeig3q_Screenshot%202026-03-24%20160542.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/32cc3czt_Screenshot%202026-03-24%20160559.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/tidlm7gb_Screenshot%202026-03-24%20160616.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ius26a19_Screenshot%202026-03-24%20160633.png",
    ],
  },
  "phone-booths": {
    name: "Phone Booths",
    slug: "phone-booths",
    category: "Privacy Solutions",
    shortDescription: "Acoustic phone booths for private calls in open office environments.",
    fullDescription: "Our Phone Booths provide a private, acoustically isolated space for calls and video conferences in open-plan offices. Featuring acoustic panel construction, mechanical ventilation, and integrated lighting, these pods support confidential conversations without disrupting colleagues.",
    specifications: [
      { label: "External Dimensions", value: "1100mm x 1100mm x 2300mm" },
      { label: "Internal Space", value: "Single occupant standing/seated" },
      { label: "Acoustic Rating", value: "Class A (28-30 dB reduction)" },
      { label: "Ventilation", value: "Built-in silent fan system" },
      { label: "Lighting", value: "LED panel with dimmer control" },
      { label: "Power", value: "2 x power outlets, 2 x USB ports" },
      { label: "Door", value: "Tempered glass with magnetic seal" },
      { label: "Frame", value: "Powder-coated aluminum" },
    ],
    features: [
      "Superior acoustic insulation",
      "Motion-activated ventilation",
      "Dimmable LED lighting",
      "Integrated power and USB charging",
      "Magnetic door seal",
      "Occupancy sensor",
    ],
    industries: ["IT & Technology", "Co-working", "BPO", "Corporate HQ"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/k23r4zhn_Screenshot%202026-03-24%20150513.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/k23r4zhn_Screenshot%202026-03-24%20150513.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/yrdl2wg2_Screenshot%202026-03-24%20153415.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/adnf5p02_Gemini_Generated_Image_uzxeunuzxeunuzxe.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/peg5equu_Screenshot%202026-03-24%20151350.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/9lef8a7u_Screenshot%202026-03-24%20151443.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/epbeheqt_Screenshot%202026-03-24%20153506.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/xejqx839_Screenshot%202026-03-24%20153437.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/2gce8rzv_Screenshot%202026-03-24%20153451.png",
    ],
  },
  "meeting-pods": {
    name: "Meeting Pods",
    slug: "meeting-pods",
    category: "Privacy Solutions",
    shortDescription: "Acoustic meeting pods for team discussions and video conferences.",
    fullDescription: "Meeting Pods offer acoustically isolated spaces for small team meetings, video conferences, and collaborative work sessions. Available in 2-person to 6-person configurations with integrated technology and comfortable seating.",
    specifications: [
      { label: "Sizes Available", value: "2-person, 4-person, 6-person" },
      { label: "4-Person Dimensions", value: "2400mm x 1500mm x 2300mm" },
      { label: "Acoustic Rating", value: "Class A (32-35 dB reduction)" },
      { label: "Seating", value: "Upholstered bench or individual seats" },
      { label: "Table", value: "Integrated work surface" },
      { label: "AV Ready", value: "Monitor mount, cable routing" },
      { label: "Ventilation", value: "HVAC-compatible fresh air system" },
      { label: "Glass", value: "Double-glazed acoustic glass panels" },
    ],
    features: [
      "Multiple size configurations",
      "Double-glazed acoustic glass",
      "Integrated AV mounting",
      "Climate control compatible",
      "Whiteboard panels available",
      "Customizable fabric finishes",
    ],
    industries: ["IT & Technology", "Corporate HQ", "Co-working"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/a32nngo8_Screenshot%202026-03-24%20145827.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/a32nngo8_Screenshot%202026-03-24%20145827.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/bpe3ukc5_Screenshot%202026-03-24%20145725.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/znvrc7ex_Screenshot%202026-03-24%20145742.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/b8scy0jd_Screenshot%202026-03-24%20145755.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ksrjtg8k_Screenshot%202026-03-24%20145812.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ng4vgxc8_Screenshot%202026-03-24%20163424.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/jrilfr05_Screenshot%202026-03-24%20145919.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ny5zjzng_Screenshot%202026-03-24%20150010.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ly1gpdc1_Screenshot%202026-03-24%20150109.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ox4oj6lq_Screenshot%202026-03-24%20162232.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/97x1pxk5_Screenshot%202026-03-24%20170433.png",
    ],
  },
  "conference-tables": {
    name: "Conference Tables",
    slug: "conference-tables",
    category: "Meeting Room Furniture",
    shortDescription: "Professional conference tables for boardrooms and meeting spaces.",
    fullDescription: "Our Conference Tables combine elegant design with practical functionality for productive meetings. Available in various sizes and finishes, with integrated power and data connectivity options to support modern meeting technology.",
    specifications: [
      { label: "Sizes", value: "6-seater to 20-seater options" },
      { label: "Standard 10-seater", value: "3000mm x 1200mm x 750mm" },
      { label: "Table Top", value: "32mm thick with ABS edge banding" },
      { label: "Finishes", value: "Veneer, laminate, or glass top options" },
      { label: "Base", value: "Steel pedestal or wooden leg options" },
      { label: "Cable Management", value: "Integrated flip-up cable boxes" },
      { label: "Power", value: "Pop-up power modules available" },
      { label: "Edge Profile", value: "Beveled, bullnose, or waterfall" },
    ],
    features: [
      "Multiple seating configurations",
      "Integrated power and data ports",
      "Premium finish options",
      "Cable management solutions",
      "Modular extension capability",
      "Wire management grommets",
    ],
    industries: ["IT & Technology"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/dphkf241_imgi_58_litebeam_3.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/dphkf241_imgi_58_litebeam_3.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/uyt3ggjl_imgi_75_litebeam_2.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ghhx9dvb_imgi_87_nova_space_2.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/9gcplnma_imgi_28_MeetingTable_1200-Square-Tubing_StraightBase.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/lzthq042_imgi_36_MeetingTable_1200-Round-Base-1024x819.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/nfv5hukx_44.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/9n0aaxpg_Gemini_Generated_Image_ejmbw4ejmbw4ejmb.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/slxm6wd1_imgi_3_BFS-TEX-TBL.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/xym6vj09_imgi_93_SPIDER-BASE-2.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/6rtooj5i_pic%205.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/azf7i0z1_pic%201.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/t2e7oeui_pic%202.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/mczeuqxc_pic%203.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/oxr2sysc_pic%204.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/swbwrisb_Screenshot%202026-03-25%20172515.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/c6ch2hs7_Screenshot%202026-03-25%20172550.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/d84d2gyk_Screenshot%202026-03-25%20172640.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/m6mu122m_Screenshot%202026-03-25%20172811.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/qomczzt7_Screenshot%202026-03-25%20172854.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/xtjdqo85_Gemini_Generated_Image_mporg6mporg6mpor.png",
    ],
  },

  // Corporate HQ Products
  "executive-desks": {
    name: "Executive Desks",
    slug: "executive-desks",
    category: "Executive Furniture",
    shortDescription: "Premium executive desks for senior management and leadership offices.",
    fullDescription: "Executive Desks are crafted for senior leadership, combining premium materials with sophisticated design. These desks make a statement of authority while providing ample workspace and storage for executive needs.",
    specifications: [
      { label: "Dimensions", value: "1800mm x 900mm x 750mm (Standard)" },
      { label: "Material", value: "Engineered wood with natural veneer finish" },
      { label: "Veneer Options", value: "Walnut, Oak, Mahogany, Teak" },
      { label: "Drawer Configuration", value: "2 box drawers + 1 filing drawer" },
      { label: "Modesty Panel", value: "Full-height with cable routing" },
      { label: "Wire Management", value: "Desk-top grommets and cable tray" },
      { label: "Hardware", value: "Soft-close mechanisms, chrome handles" },
      { label: "Return Unit", value: "Optional side return available" },
    ],
    features: [
      "Premium veneer finishes",
      "Integrated wire management",
      "Soft-close drawer mechanisms",
      "Optional side return unit",
      "Matching credenza available",
      "Premium hardware fittings",
    ],
    industries: ["Corporate HQ", "Banking", "Public Sector"],
    image: "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/0h39uvqs_Pic%2018.png",
    images: [
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/0h39uvqs_Pic%2018.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/rugl8qeo_imgi_4_Rangecombo-MISSION-Transparent.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/p0orltj7_64.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/pp55q4cb_Gemini_Generated_Image_eke81weke81weke8.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/7q0ojmy5_Gemini_Generated_Image_kpx7a4kpx7a4kpx7.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/lcc8383v_imgi_3_Mission-Exec-Workstation-2000x1800-in-Sahara-Storm-Grey-Front-View-1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/kwm1e8di_imgi_9_ACTIVITY-System-S-Sahara-Loop-Leg-Workstation-with-Hinged-Door-Credenza-back-View-4.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/8425xbbn_imgi_5_Core-O-WhiteArden-Managers-Desk-Server-Kori.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/qm82r6v2_imgi_6_Core-O-CharcoalBrookhill-Managers-Desk-Server-1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/adnxeqpt_imgi_7_Core-U-BlackBrookhill-Managers-Desk-Server-1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/453bdosk_imgi_8_Core-U-CharcoalBrookhill-Managers-1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/o1dwz0du_imgi_93_Slope-L-shape-desk.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/qpjn68du_imgi_10_Rangecombo-ACTIVITY-Transparent.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/tq9sqp9f_imgi_40_Woodline-BlackBrookhill-2dr-3dw-Server-Front.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/1twuj30k_imgi_81_Gateway-New-Exec.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/ab1k9wsl_imgi_82_ACTIVITY-Sahara-Single-Desk-1500x750-System-no-storage-with-shadow-1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/lzkm2870_imgi_259_925-Single-Desk-2-Draw-Fitted.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/lotlmsk9_imgi_135_Milano-L-shape-front.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/8gc3g7ia_imgi_171_savona-chestnut.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/s03fusdu_imgi_213_Woodline-BlackBrookhill-2dr-3dw-Server-Front.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/jptwz5du_imgi_222_Woodline-BlackBrookhill-2dr-3dw-Back-nochair-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/oxhpe8gi_pic%2013.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/jcvvtnjo_pic%2015.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/wucd6b9i_pic%2016.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/j5krob4w_pic%2017.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/cu7he0wg_pic%2023.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/4unzzx5v_pic%2019.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/zn0efa3d_pic%2020.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/yxtvb86q_pic%2021.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/cv82ddx3_pic%2022.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/nrt9rwz3_pic%2028.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/bz2yato9_Pic%2024.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/zoht0h1e_pic%2025.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/wkp4x075_pic%2026.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/vol2ofzd_pic%2027.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/2m4r3px1_pic%2030.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/23efgd1y_pic%2029.png",
      "/media/executive-desks/executive-desk-001.jpg",
      "/media/executive-desks/executive-desk-002.jpg",
      "/media/executive-desks/executive-desk-003.jpg",
      "/media/executive-desks/executive-desk-004.jpg",
      "/media/executive-desks/executive-desk-005.jpg",
      "/media/executive-desks/executive-desk-006.jpg",
      "/media/executive-desks/executive-desk-007.jpg",
      "/media/executive-desks/executive-desk-008.jpg",
      "/media/executive-desks/executive-desk-009.jpg",
      "/media/executive-desks/executive-desk-010.jpg",
      "/media/executive-desks/executive-desk-011.jpg",
      "/media/executive-desks/executive-desk-012.jpg",
      "/media/executive-desks/executive-desk-013.jpg",
      "/media/executive-desks/executive-desk-014.jpg",
      "/media/executive-desks/executive-desk-015.jpg",
      "/media/executive-desks/executive-desk-016.jpg",
      "/media/executive-desks/executive-desk-017.jpg",
      "/media/executive-desks/executive-desk-018.jpg",
      "/media/executive-desks/executive-desk-019.jpg",
      "/media/executive-desks/executive-desk-020.jpg",
      "/media/executive-desks/executive-desk-021.jpg",
      "/media/executive-desks/executive-desk-022.jpg",
      "/media/executive-desks/executive-desk-023.jpg",
      "/media/executive-desks/executive-desk-024.jpg",
      "/media/executive-desks/executive-desk-025.jpg",
      "/media/executive-desks/executive-desk-026.jpg",
      "/media/executive-desks/executive-desk-027.jpg",
      "/media/executive-desks/executive-desk-028.jpg",
      "/media/executive-desks/executive-desk-029.jpg",
      "/media/executive-desks/executive-desk-030.jpg",
      "/media/executive-desks/executive-desk-031.jpg",
      "/media/executive-desks/executive-desk-032.jpg",
      "/media/executive-desks/executive-desk-033.jpg",
      "/media/executive-desks/executive-desk-034.jpg",
      "/media/executive-desks/executive-desk-035.jpg",
      "/media/executive-desks/executive-desk-036.jpg",
      "/media/executive-desks/executive-desk-037.jpg",
      "/media/executive-desks/executive-desk-038.jpg",
      "/media/executive-desks/executive-desk-039.jpg",
      "/media/executive-desks/executive-desk-040.jpg",
      "/media/executive-desks/executive-desk-041.jpg",
      "/media/executive-desks/executive-desk-042.jpg",
      "/media/executive-desks/executive-desk-043.jpg",
      "/media/executive-desks/executive-desk-044.jpg",
      "/media/executive-desks/executive-desk-045.jpg",
      "/media/executive-desks/executive-desk-046.jpg",
      "/media/executive-desks/executive-desk-047.jpg",
      "/media/executive-desks/executive-desk-048.jpg",
      "/media/executive-desks/executive-desk-049.jpg",
      "/media/executive-desks/executive-desk-050.jpg",
      "/media/executive-desks/executive-desk-051.jpg",
      "/media/executive-desks/executive-desk-052.jpg",
      "/media/executive-desks/executive-desk-053.jpg",
      "/media/executive-desks/executive-desk-054.jpg",
      "/media/executive-desks/executive-desk-055.jpg",
      "/media/executive-desks/executive-desk-056.jpg",
      "/media/executive-desks/executive-desk-057.jpg",
      "/media/executive-desks/executive-desk-058.jpg",
      "/media/executive-desks/executive-desk-059.jpg",
      "/media/executive-desks/executive-desk-060.jpg",
      "/media/executive-desks/executive-desk-061.jpg",
      "/media/executive-desks/executive-desk-062.jpg",
      "/media/executive-desks/executive-desk-063.jpg",
      "/media/executive-desks/executive-desk-064.jpg",
      "/media/executive-desks/executive-desk-065.jpg",
      "/media/executive-desks/executive-desk-066.jpg",
      "/media/executive-desks/executive-desk-067.jpg",
      "/media/executive-desks/executive-desk-068.jpg",
      "/media/executive-desks/executive-desk-069.jpg",
      "/media/executive-desks/executive-desk-070.jpg",
      "/media/executive-desks/executive-desk-071.jpg",
      "/media/executive-desks/executive-desk-072.jpg",
      "/media/executive-desks/executive-desk-073.jpg",
      "/media/executive-desks/executive-desk-074.jpg",
      "/media/executive-desks/executive-desk-075.jpg",
      "/media/executive-desks/executive-desk-076.jpg",
      "/media/executive-desks/executive-desk-077.jpg",
      "/media/executive-desks/executive-desk-078.jpg",
      "/media/executive-desks/executive-desk-079.jpg",
      "/media/executive-desks/executive-desk-080.jpg",
      "/media/executive-desks/executive-desk-081.jpg",
      "/media/executive-desks/executive-desk-082.jpg",
      "/media/executive-desks/executive-desk-083.jpg",
      "/media/executive-desks/executive-desk-084.jpg",
      "/media/executive-desks/executive-desk-085.jpg",
      "/media/executive-desks/executive-desk-086.jpg",
      "/media/executive-desks/executive-desk-087.jpg",
      "/media/executive-desks/executive-desk-088.jpg",
      "/media/executive-desks/executive-desk-089.jpg",
      "/media/executive-desks/executive-desk-090.jpg",
      "/media/executive-desks/executive-desk-091.jpg",
      "/media/executive-desks/executive-desk-092.jpg",
      "/media/executive-desks/executive-desk-093.jpg",
      "/media/executive-desks/executive-desk-094.jpg",
      "/media/executive-desks/executive-desk-095.jpg",
      "/media/executive-desks/executive-desk-096.jpg",
      "/media/executive-desks/executive-desk-097.jpg",
      "/media/executive-desks/executive-desk-098.jpg",
      "/media/executive-desks/executive-desk-099.jpg",
      "/media/executive-desks/executive-desk-100.jpg",
      "/media/executive-desks/executive-desk-101.jpg",
      "/media/executive-desks/executive-desk-102.jpg",
      "/media/executive-desks/executive-desk-103.jpg",
      "/media/executive-desks/executive-desk-104.jpg",
      "/media/executive-desks/executive-desk-105.jpg",
      "/media/executive-desks/executive-desk-106.jpg",
      "/media/executive-desks/executive-desk-107.jpg",
      "/media/executive-desks/executive-desk-108.jpg",
      "/media/executive-desks/executive-desk-109.jpg",
      "/media/executive-desks/executive-desk-110.jpg",
      "/media/executive-desks/executive-desk-111.jpg",
      "/media/executive-desks/executive-desk-112.jpg",
      "/media/executive-desks/executive-desk-113.jpg",
      "/media/executive-desks/executive-desk-114.jpg",
      "/media/executive-desks/executive-desk-115.jpg",
      "/media/executive-desks/executive-desk-116.jpg",
    ],
  },
  "boardroom-tables": {
    name: "Boardroom Tables",
    slug: "boardroom-tables",
    category: "Executive Furniture",
    shortDescription: "Prestigious boardroom tables for corporate decision-making spaces.",
    fullDescription: "Our Boardroom Tables are designed for the most important meetings. Featuring premium materials, sophisticated design, and integrated technology, these tables create an environment befitting high-stakes corporate discussions.",
    specifications: [
      { label: "Standard Sizes", value: "12-seater to 30-seater" },
      { label: "12-Seater Dimensions", value: "4200mm x 1500mm x 750mm" },
      { label: "Top Material", value: "Premium veneer or solid surface" },
      { label: "Top Thickness", value: "40mm with solid wood edge" },
      { label: "Base", value: "Custom designed steel or wood" },
      { label: "Cable Management", value: "Motorized pop-up units" },
      { label: "Power Integration", value: "Built-in power and AV connectivity" },
      { label: "Finish", value: "High-gloss lacquer or matte" },
    ],
    features: [
      "Bespoke design options",
      "Motorized cable management",
      "Integrated AV systems",
      "Premium veneer selection",
      "Custom sizing available",
      "Matching credenza units",
    ],
    industries: ["Corporate HQ", "Banking", "Public Sector"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/oxr2sysc_pic%204.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/oxr2sysc_pic%204.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/6rtooj5i_pic%205.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/swbwrisb_Screenshot%202026-03-25%20172515.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/c6ch2hs7_Screenshot%202026-03-25%20172550.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/d84d2gyk_Screenshot%202026-03-25%20172640.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/m6mu122m_Screenshot%202026-03-25%20172811.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/qomczzt7_Screenshot%202026-03-25%20172854.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/xtjdqo85_Gemini_Generated_Image_mporg6mporg6mpor.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/azf7i0z1_pic%201.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/t2e7oeui_pic%202.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/mczeuqxc_pic%203.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/9n0aaxpg_Gemini_Generated_Image_ejmbw4ejmbw4ejmb.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/9gcplnma_imgi_28_MeetingTable_1200-Square-Tubing_StraightBase.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/lzthq042_imgi_36_MeetingTable_1200-Round-Base-1024x819.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/nfv5hukx_44.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/slxm6wd1_imgi_3_BFS-TEX-TBL.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/xym6vj09_imgi_93_SPIDER-BASE-2.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/dphkf241_imgi_58_litebeam_3.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/uyt3ggjl_imgi_75_litebeam_2.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ghhx9dvb_imgi_87_nova_space_2.png",
    ],
  },
  "meeting-tables": {
    name: "Meeting Tables",
    slug: "meeting-tables",
    category: "Public Sector Furniture",
    shortDescription: "Robust meeting tables designed for public sector offices and Public sector conference rooms.",
    fullDescription: "Our Meeting Tables are engineered for public sector environments, combining institutional durability with professional aesthetics. Built to public sector procurement standards, these tables support large-format meetings with integrated connectivity and heavy-duty construction.",
    specifications: [
      { label: "Sizes", value: "8-seater to 24-seater options" },
      { label: "Standard 12-seater", value: "3600mm x 1400mm x 750mm" },
      { label: "Table Top", value: "36mm thick with PVC edge banding" },
      { label: "Finishes", value: "Laminate or veneer with scratch-resistant surface" },
      { label: "Base", value: "Heavy-duty steel pedestal or panel base" },
      { label: "Cable Management", value: "Flip-up cable boxes with power modules" },
      { label: "Load Capacity", value: "High load bearing construction" },
      { label: "Compliance", value: "Standard public sector specifications" },
    ],
    features: [
      "Public sector specification compliant",
      "Heavy-duty construction",
      "Integrated power and data ports",
      "Scratch and stain resistant surfaces",
      "Modular configurations available",
      "Public sector procurement compatible",
    ],
    industries: ["Public Sector"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/m6mu122m_Screenshot%202026-03-25%20172811.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/m6mu122m_Screenshot%202026-03-25%20172811.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/9gcplnma_imgi_28_MeetingTable_1200-Square-Tubing_StraightBase.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/lzthq042_imgi_36_MeetingTable_1200-Round-Base-1024x819.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/nfv5hukx_44.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/9n0aaxpg_Gemini_Generated_Image_ejmbw4ejmbw4ejmb.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/slxm6wd1_imgi_3_BFS-TEX-TBL.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/xym6vj09_imgi_93_SPIDER-BASE-2.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/dphkf241_imgi_58_litebeam_3.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/uyt3ggjl_imgi_75_litebeam_2.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ghhx9dvb_imgi_87_nova_space_2.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/6rtooj5i_pic%205.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/azf7i0z1_pic%201.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/t2e7oeui_pic%202.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/mczeuqxc_pic%203.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/oxr2sysc_pic%204.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/swbwrisb_Screenshot%202026-03-25%20172515.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/c6ch2hs7_Screenshot%202026-03-25%20172550.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/d84d2gyk_Screenshot%202026-03-25%20172640.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/qomczzt7_Screenshot%202026-03-25%20172854.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/xtjdqo85_Gemini_Generated_Image_mporg6mporg6mpor.png",
      "/media/meeting-tables/meeting-table-001.jpg",
      "/media/meeting-tables/meeting-table-002.jpg",
      "/media/meeting-tables/meeting-table-003.jpg",
      "/media/meeting-tables/meeting-table-004.jpg",
      "/media/meeting-tables/meeting-table-005.jpg",
      "/media/meeting-tables/meeting-table-006.jpg",
      "/media/meeting-tables/meeting-table-007.jpg",
      "/media/meeting-tables/meeting-table-008.jpg",
      "/media/meeting-tables/meeting-table-009.jpg",
      "/media/meeting-tables/meeting-table-010.jpg",
      "/media/meeting-tables/meeting-table-011.jpg",
      "/media/meeting-tables/meeting-table-012.jpg",
      "/media/meeting-tables/meeting-table-013.jpg",
      "/media/meeting-tables/meeting-table-014.jpg",
      "/media/meeting-tables/meeting-table-015.jpg",
      "/media/meeting-tables/meeting-table-016.jpg",
      "/media/meeting-tables/meeting-table-017.jpg",
      "/media/meeting-tables/meeting-table-018.jpg",
      "/media/meeting-tables/meeting-table-019.jpg",
      "/media/meeting-tables/meeting-table-020.jpg",
      "/media/meeting-tables/meeting-table-021.jpg",
      "/media/meeting-tables/meeting-table-022.jpg",
      "/media/meeting-tables/meeting-table-023.jpg",
      "/media/meeting-tables/meeting-table-024.jpg",
      "/media/meeting-tables/meeting-table-025.jpg",
      "/media/meeting-tables/meeting-table-026.jpg",
      "/media/meeting-tables/meeting-table-027.jpg",
      "/media/meeting-tables/meeting-table-028.jpg",
      "/media/meeting-tables/meeting-table-029.jpg",
      "/media/meeting-tables/meeting-table-030.jpg",
      "/media/meeting-tables/meeting-table-031.jpg",
      "/media/meeting-tables/meeting-table-032.jpg",
      "/media/meeting-tables/meeting-table-033.jpg",
      "/media/meeting-tables/meeting-table-034.jpg",
      "/media/meeting-tables/meeting-table-035.jpg",
      "/media/meeting-tables/meeting-table-036.jpg",
      "/media/meeting-tables/meeting-table-037.jpg",
      "/media/meeting-tables/meeting-table-038.jpg",
      "/media/meeting-tables/meeting-table-039.jpg",
      "/media/meeting-tables/meeting-table-040.jpg",
      "/media/meeting-tables/meeting-table-041.jpg",
      "/media/meeting-tables/meeting-table-042.jpg",
      "/media/meeting-tables/meeting-table-043.jpg",
      "/media/meeting-tables/meeting-table-044.jpg",
      "/media/meeting-tables/meeting-table-045.jpg",
      "/media/meeting-tables/meeting-table-046.jpg",
      "/media/meeting-tables/meeting-table-047.jpg",
      "/media/meeting-tables/meeting-table-048.jpg",
      "/media/meeting-tables/meeting-table-049.jpg",
      "/media/meeting-tables/meeting-table-050.jpg",
      "/media/meeting-tables/meeting-table-051.jpg",
      "/media/meeting-tables/meeting-table-052.jpg",
      "/media/meeting-tables/meeting-table-053.jpg",
      "/media/meeting-tables/meeting-table-054.jpg",
      "/media/meeting-tables/meeting-table-055.jpg",
      "/media/meeting-tables/meeting-table-056.jpg",
      "/media/meeting-tables/meeting-table-057.jpg",
      "/media/meeting-tables/meeting-table-058.jpg",
      "/media/meeting-tables/meeting-table-059.jpg",
      "/media/meeting-tables/meeting-table-060.jpg",
      "/media/meeting-tables/meeting-table-061.jpg",
      "/media/meeting-tables/meeting-table-062.jpg",
      "/media/meeting-tables/meeting-table-063.jpg",
      "/media/meeting-tables/meeting-table-064.jpg",
      "/media/meeting-tables/meeting-table-065.jpg",
      "/media/meeting-tables/meeting-table-066.jpg",
      "/media/meeting-tables/meeting-table-067.jpg",
      "/media/meeting-tables/meeting-table-068.jpg",
      "/media/meeting-tables/meeting-table-069.jpg",
      "/media/meeting-tables/meeting-table-070.jpg",
      "/media/meeting-tables/meeting-table-071.jpg",
      "/media/meeting-tables/meeting-table-072.jpg",
      "/media/meeting-tables/meeting-table-073.jpg",
      "/media/meeting-tables/meeting-table-074.jpg",
      "/media/meeting-tables/meeting-table-075.jpg",
      "/media/meeting-tables/meeting-table-076.jpg",
      "/media/meeting-tables/meeting-table-077.jpg",
      "/media/meeting-tables/meeting-table-078.jpg",
      "/media/meeting-tables/meeting-table-079.jpg",
      "/media/meeting-tables/meeting-table-080.jpg",
      "/media/meeting-tables/meeting-table-081.jpg",
      "/media/meeting-tables/meeting-table-082.jpg",
      "/media/meeting-tables/meeting-table-083.jpg",
      "/media/meeting-tables/meeting-table-084.jpg",
      "/media/meeting-tables/meeting-table-085.jpg",
      "/media/meeting-tables/meeting-table-086.jpg",
      "/media/meeting-tables/meeting-table-087.jpg",
      "/media/meeting-tables/meeting-table-088.jpg",
      "/media/meeting-tables/meeting-table-089.jpg",
      "/media/meeting-tables/meeting-table-090.jpg",
      "/media/meeting-tables/meeting-table-091.jpg",
      "/media/meeting-tables/meeting-table-092.jpg",
      "/media/meeting-tables/meeting-table-093.jpg",
      "/media/meeting-tables/meeting-table-094.jpg",
      "/media/meeting-tables/meeting-table-095.jpg",
      "/media/meeting-tables/meeting-table-096.jpg",
      "/media/meeting-tables/meeting-table-097.jpg",
      "/media/meeting-tables/meeting-table-098.jpg",
      "/media/meeting-tables/meeting-table-099.jpg",
      "/media/meeting-tables/meeting-table-100.jpg",
    ],
  },
  "reception-counters": {
    category: "Front Office",
    shortDescription: "Impressive reception counters for corporate lobbies and entrance areas.",
    fullDescription: "Reception Counters create the first impression of your organization. Our designs combine aesthetic appeal with functional workspace, featuring transaction tops, storage, and ADA-compliant heights for inclusive visitor management.",
    specifications: [
      { label: "Length Options", value: "1800mm to 4800mm (modular)" },
      { label: "Counter Height", value: "1100mm (transaction), 750mm (accessible)" },
      { label: "Work Surface Depth", value: "600mm behind counter" },
      { label: "Material", value: "Engineered wood with DuPont Corian top" },
      { label: "Front Panel", value: "Laminate, veneer, or backlit options" },
      { label: "Storage", value: "Integrated drawers and cabinets" },
      { label: "Cable Management", value: "Built-in wire routing" },
      { label: "Customization", value: "Logo integration, lighting effects" },
    ],
    features: [
      "Modular configuration options",
      "ADA compliant heights",
      "Integrated storage solutions",
      "Backlit panel options",
      "Logo customization",
      "Durable transaction surfaces",
    ],
    industries: ["Corporate HQ", "Banking", "Public Sector"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/nzjaj9qj_Screenshot%202026-03-16%20135949.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/nzjaj9qj_Screenshot%202026-03-16%20135949.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ek6kftbx_pic%2010.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/lw242s7o_pic%206.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/s7tg7stu_pic%207.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/vmfr8xlo_pic%208.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/izo5vro3_pic%209.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/q43wlohn_Screenshot%202026-03-16%20140222.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/fnynldud_pic%2011.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/sy7ejc0t_pic%2012.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/n3pu9mdz_pic%2014.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/m4l6aoxl_Screenshot%202026-03-16%20140616.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/3fyiiww1_Screenshot%202026-03-16%20140631.png",
      "/media/reception-counters/reception-counter-13.jpg",
      "/media/reception-counters/reception-counter-14.jpg",
      "/media/reception-counters/reception-counter-15.jpg",
      "/media/reception-counters/reception-counter-16.jpg",
    ],
  },
  "executive-chairs": {
    name: "Executive Chairs",
    slug: "executive-chairs",
    category: "Executive Furniture",
    shortDescription: "Premium executive seating with superior comfort and ergonomic support.",
    fullDescription: "Executive Chairs combine prestige with ergonomic excellence. Featuring premium leather upholstery, synchronized tilt mechanisms, and lumbar support, these chairs provide all-day comfort for senior leadership.",
    specifications: [
      { label: "Seat Height Range", value: "450mm to 550mm (adjustable)" },
      { label: "Seat Width", value: "520mm" },
      { label: "Backrest Height", value: "High back (700mm) or Mid back (550mm)" },
      { label: "Upholstery", value: "Genuine leather or premium fabric" },
      { label: "Mechanism", value: "Synchronized tilt with tension control" },
      { label: "Armrests", value: "4D adjustable padded armrests" },
      { label: "Base", value: "Polished aluminum 5-star base" },
      { label: "Casters", value: "Soft or hard floor options" },
    ],
    features: [
      "Genuine leather upholstery",
      "Synchronized tilt mechanism",
      "Adjustable lumbar support",
      "4D armrest adjustment",
      "Polished aluminum base",
      "ISO 9001:2015 certified ergonomics",
    ],
    industries: ["Corporate HQ", "Banking", "Executive Offices"],
    image: "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/q2fhpp2z_imgi_15_Bruno-highback.png",
    images: [
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/q2fhpp2z_imgi_15_Bruno-highback.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/fm3o545g_imgi_5_Alexis_1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/j4vjlien_imgi_13_Athena-midback.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/e191ma2f_imgi_14_Athena-family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/pm9q6yez_imgi_17_ceto_highback_03.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/ak4nqa6i_imgi_18_ceto_family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/7vh79z3w_imgi_26_Diana_01.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/0mmuqppm_imgi_23_Lucia_master.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/pdi5jq45_imgi_25_Dayna.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/8uqya98i_imgi_28_Eduardo.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/i9im8109_imgi_29_Eduardo-visitor.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/lhlz3e22_imgi_32_Logitech_embody_front.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/vhksr0av_imgi_34_Emma_Hb_front.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/1sztmw3s_imgi_35_Emma_Family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/9haqab9v_imgi_36_Eros_1-1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/88313jiv_imgi_37_Eros_2.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/fl5ulh96_imgi_38_Eve_1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/hab5mkg5_imgi_39_Eve_Family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/jid5kgxl_imgi_40_Freya_1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/ey0ca6t4_imgi_48_Hunter_1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/fc7833oe_imgi_50_Jane_01.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/co3s361u_imgi_53_Kendall_family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/dzmrcim8_imgi_54_Kori-Hb_Front.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/r0mxz5bp_imgi_56_Kylie_Highback.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/paftc42z_imgi_57_Kylie_Family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/4ffqnv84_imgi_61_Lauren_Family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/wvnydap4_imgi_66_Luca-highback.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/f0evq7pe_imgi_67_luca_family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/w7qeu1rf_imgi_68_Mateo-Highback.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/h059jqit_imgi_78_Monica_01.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/zsbls9ul_imgi_79_Monica_Family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/ugc91cn9_imgi_80_Nemesis_highback.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/uunp876a_imgi_81_Nemesis_family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/5zdxokjy_imgi_82_Noah_02.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/fv0liup8_imgi_83_Noah_Family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/mgvteuuq_imgi_84_Nova-grey.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/2dy4blmt_imgi_85_Nova_Family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/e4lzysw5_imgi_88_Phoenix_7-1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/bzpcybmo_imgi_90_Polly.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/v0j9n2rn_imgi_93_Roberto_1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/xez10p06_imgi_105_Sweetheart-Swivel-1_800px_new.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/3qcja90t_imgi_111_Titan.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/v1z9zn4o_imgi_112_vera.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/qhaj38gl_imgi_116_wyatt_family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/s38sg2dv_imgi_117_Zero_G_01.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/gmkqxgd8_imgi_141_Alexis_1-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/t0s6ql4v_imgi_186_Athena-midback-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/fxsomsmj_imgi_191_Athena-family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/p8xuglp1_imgi_196_Bruno-highback-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/fsdxt0w3_imgi_207_ceto_highback_03-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/ee36rj7w_imgi_212_ceto_family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/vz9o6ps3_imgi_235_Lucia_master-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/ljz5jvxe_imgi_249_Diana_01-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/i3rm9nek_imgi_259_Eduardo-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/c6nq1rjc_imgi_264_Eduardo-visitor-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/h3eacn1v_imgi_293_Emma_Hb_front-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/v7rby07q_imgi_298_Emma_Family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/1dd0lhfc_imgi_303_Eros_1-1-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/hdxqc5r2_imgi_308_Eros_2-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/0asbkl6v_imgi_313_Eve_1-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/0rb8hmsq_imgi_318_Eve_Family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/2y6irmrb_imgi_323_Freya_1-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/gkcwwxp8_imgi_362_Hunter_1-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/sx97ihbk_imgi_374_Jane_01-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/i4jr0px3_imgi_390_Kendall_family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/ls08aug9_imgi_395_Kori-Hb_Front-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/rxdymwjc_imgi_404_Kylie_Highback-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/6ys5jru8_imgi_409_Kylie_Family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/k60xbcq2_imgi_431_Lauren_Family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/z8qrtwo6_imgi_460_Luca-highback-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/lr1cttgn_imgi_465_luca_family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/73jo07et_imgi_470_Mateo-Highback-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/w3k0nlzu_imgi_519_Monica_01-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/63ikm83w_imgi_524_Monica_Family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/n7k62pu6_imgi_529_Nemesis_highback-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/imn0jiwe_imgi_534_Nemesis_family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/v4g6eae2_imgi_539_Noah_02-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/el61pxth_imgi_544_Noah_Family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/0j4966zp_imgi_549_Nova-grey-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/gb6rwmry_imgi_554_Nova_Family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/4oac7mdi_imgi_569_Phoenix_7-1-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/sp5fpef3_imgi_594_Roberto_1-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/si3dl17e_imgi_662_Sweetheart-Swivel-1_800px_new-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/vgb2k8n6_imgi_698_Titan-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/6hsjihbo_imgi_703_vera-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/ax5smjd3_imgi_723_wyatt_family-768x768.png",
    ],
  },
  "filing-systems": {
    name: "Filing Systems",
    slug: "filing-systems",
    category: "Storage Solutions",
    shortDescription: "Organized filing and storage systems for document management.",
    fullDescription: "Our Filing Systems provide efficient document storage with a range of cabinet types and configurations. From lateral files to mobile pedestals, we offer solutions that maximize storage while maintaining accessibility.",
    specifications: [
      { label: "Types", value: "Lateral, vertical, mobile pedestals" },
      { label: "Lateral File Sizes", value: "2-drawer, 3-drawer, 4-drawer" },
      { label: "Material", value: "CRCA steel, powder coated" },
      { label: "Drawer Load", value: "40kg per drawer" },
      { label: "Lock", value: "Central locking with 2 keys" },
      { label: "Drawer Extension", value: "Full extension ball bearing slides" },
      { label: "Fire Rating", value: "Fire-resistant options available" },
      { label: "Colors", value: "Standard RAL colors or custom" },
    ],
    features: [
      "Full-extension drawer slides",
      "Anti-tilt safety mechanism",
      "Central locking system",
      "Label holders included",
      "Fire-resistant options",
      "Modular stacking capability",
    ],
    industries: ["Corporate HQ", "Public Sector", "Banking"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/901rnosj_imgi_7_Sahara-Storm-Grey-Melamine-Door-Bookcase-1600-4-Tier-1.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/901rnosj_imgi_7_Sahara-Storm-Grey-Melamine-Door-Bookcase-1600-4-Tier-1.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/u94iyd54_imgi_8_Sahara-Storm-Grey-Server-with-4-Door-1600x450x800h-1.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/2d8luyuc_imgi_23_Sahara-Storm-Grey-Wall-Unit-2-Door-4-Drawer-1.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/iqcdjcgh_imgi_25_Sahara-Storm-Grey-Open-Bookcase-1600-4-Tier.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/gc0ga9hf_imgi_3_BFS-LKR-RNG-2.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/c1tip16q_imgi_4_BFS-LKR-RNG-3.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/20zicj2q_imgi_5_BFS-LKR-RNG-4.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/5ymiu70o_imgi_6_BFS-LKR-RNG-7.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/8c4u5hds_imgi_41_BFS-LKR-RNG-8.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/cqf4doq9_imgi_37_BFS-LKR-RNG-7-600x600.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/iv5vemz7_imgi_50_Sahara-Storm-Grey-Server-with-4-Door-1600x450x800h-1-768x768.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/se8uej32_Screenshot%202026-03-26%20121641.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/pze2bvag_Screenshot%202026-03-26%20121745.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/aufywm0o_Screenshot%202026-03-16%20140653.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/0ydhah5q_Screenshot%202026-03-25%20115341.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/0m2lpjwc_Screenshot%202026-03-25%20115428.png",
    ],
  },

  // Co-working Products
  "hot-desks": {
    name: "Hot Desks",
    slug: "hot-desks",
    category: "Flexible Workstations",
    shortDescription: "Versatile hot desking solutions for co-working and flexible work environments.",
    fullDescription: "Hot Desks are designed for the modern flexible workplace where different users occupy the same workspace at different times. Durable, easy to clean, and featuring integrated power, these desks support the co-working lifestyle.",
    specifications: [
      { label: "Standard Size", value: "1200mm x 600mm x 750mm" },
      { label: "Material", value: "High-pressure laminate on PLPB" },
      { label: "Leg Frame", value: "Powder-coated steel" },
      { label: "Power Integration", value: "Desktop power module with USB" },
      { label: "Privacy Screen", value: "Optional desk-mounted screens" },
      { label: "Bag Hook", value: "Integrated under-desk hook" },
      { label: "Surface", value: "Anti-bacterial laminate option" },
      { label: "Weight Capacity", value: "60kg uniformly distributed" },
    ],
    features: [
      "Anti-bacterial surface options",
      "Integrated power and USB",
      "Easy-clean laminate surface",
      "Optional privacy screens",
      "Personal storage solutions",
      "Cable management included",
    ],
    industries: ["Co-working", "Start-ups", "IT & Technology"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/6j4wvxn5_Screenshot%202026-03-26%20133049.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/6j4wvxn5_Screenshot%202026-03-26%20133049.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/md9et6y8_Screenshot%202026-03-26%20132507.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/xp3jq7bq_Screenshot%202026-03-26%20132532.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/fpzk3057_Screenshot%202026-03-26%20132549.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/r4xo9for_Screenshot%202026-03-26%20132607.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/gimzln2l_Screenshot%202026-03-26%20133246.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/a0kohiuf_Screenshot%202026-03-26%20133114.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/61jxihf5_Screenshot%202026-03-26%20133130.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/mtlvzfho_Screenshot%202026-03-26%20133157.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/t7jctmr8_Screenshot%202026-03-26%20133223.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ce0s79ex_Screenshot%202026-03-26%20133331.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ucd7351l_Screenshot%202026-03-26%20133451.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/41oqnv5b_Screenshot%202026-03-26%20133602.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/1k1ci6xh_Screenshot%202026-03-26%20133734.png",
    ],
  },
  "meeting-booths": {
    name: "Meeting Booths",
    slug: "meeting-booths",
    category: "Privacy Solutions",
    shortDescription: "Semi-private booth seating for informal meetings and collaboration.",
    fullDescription: "Meeting Booths provide semi-private spaces for quick discussions, informal meetings, and collaborative work. With high-back upholstered panels and integrated tables, they create defined zones within open-plan spaces.",
    specifications: [
      { label: "Configurations", value: "2-person, 4-person, 6-person" },
      { label: "4-Person Dimensions", value: "2000mm x 1400mm x 1350mm (H)" },
      { label: "Seat Height", value: "450mm" },
      { label: "Panel Height", value: "1350mm from floor" },
      { label: "Upholstery", value: "Commercial-grade fabric" },
      { label: "Table", value: "Integrated laminate or veneer top" },
      { label: "Power", value: "Optional integrated power module" },
      { label: "Acoustic Rating", value: "NRC 0.65 minimum" },
    ],
    features: [
      "High-back acoustic panels",
      "Commercial-grade upholstery",
      "Integrated work surface",
      "Optional power integration",
      "Customizable fabric colors",
      "Easy access seating",
    ],
    industries: ["Co-working", "Corporate HQ", "IT & Technology"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ox4oj6lq_Screenshot%202026-03-24%20162232.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ox4oj6lq_Screenshot%202026-03-24%20162232.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/a32nngo8_Screenshot%202026-03-24%20145827.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/bpe3ukc5_Screenshot%202026-03-24%20145725.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/znvrc7ex_Screenshot%202026-03-24%20145742.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/b8scy0jd_Screenshot%202026-03-24%20145755.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ksrjtg8k_Screenshot%202026-03-24%20145812.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ng4vgxc8_Screenshot%202026-03-24%20163424.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/jrilfr05_Screenshot%202026-03-24%20145919.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ny5zjzng_Screenshot%202026-03-24%20150010.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ly1gpdc1_Screenshot%202026-03-24%20150109.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/97x1pxk5_Screenshot%202026-03-24%20170433.png",
    ],
  },
  "locker-systems": {
    name: "Locker Systems",
    slug: "locker-systems",
    category: "Storage Solutions",
    shortDescription: "Secure personal storage lockers for flexible and hot-desking environments.",
    fullDescription: "Locker Systems provide secure personal storage in flexible work environments. Available in various sizes and locking mechanisms, from digital keypads to RFID, these lockers support hot-desking and activity-based working.",
    specifications: [
      { label: "Configurations", value: "6-door, 9-door, 12-door units" },
      { label: "Compartment Sizes", value: "300x300mm, 300x450mm, 300x600mm" },
      { label: "Material", value: "CRCA steel, powder coated" },
      { label: "Lock Options", value: "Key, combination, digital, RFID" },
      { label: "Charging", value: "USB charging locker options" },
      { label: "Colors", value: "RAL colors or custom powder coat" },
      { label: "Ventilation", value: "Perforated doors available" },
      { label: "Installation", value: "Free-standing or wall-mounted" },
    ],
    features: [
      "Multiple lock type options",
      "USB charging capabilities",
      "Ventilated door options",
      "Customizable configurations",
      "Powder-coated finishes",
      "Stackable units available",
    ],
    industries: ["Co-working", "Corporate HQ", "Industrial"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/gc0ga9hf_imgi_3_BFS-LKR-RNG-2.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/gc0ga9hf_imgi_3_BFS-LKR-RNG-2.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/c1tip16q_imgi_4_BFS-LKR-RNG-3.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/20zicj2q_imgi_5_BFS-LKR-RNG-4.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/5ymiu70o_imgi_6_BFS-LKR-RNG-7.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/8c4u5hds_imgi_41_BFS-LKR-RNG-8.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/cqf4doq9_imgi_37_BFS-LKR-RNG-7-600x600.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/901rnosj_imgi_7_Sahara-Storm-Grey-Melamine-Door-Bookcase-1600-4-Tier-1.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/u94iyd54_imgi_8_Sahara-Storm-Grey-Server-with-4-Door-1600x450x800h-1.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/2d8luyuc_imgi_23_Sahara-Storm-Grey-Wall-Unit-2-Door-4-Drawer-1.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/iqcdjcgh_imgi_25_Sahara-Storm-Grey-Open-Bookcase-1600-4-Tier.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/iv5vemz7_imgi_50_Sahara-Storm-Grey-Server-with-4-Door-1600x450x800h-1-768x768.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/se8uej32_Screenshot%202026-03-26%20121641.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/pze2bvag_Screenshot%202026-03-26%20121745.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/aufywm0o_Screenshot%202026-03-16%20140653.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/0ydhah5q_Screenshot%202026-03-25%20115341.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/0m2lpjwc_Screenshot%202026-03-25%20115428.png",
    ],
  },
  "breakout-furniture": {
    name: "Breakout Furniture",
    slug: "breakout-furniture",
    category: "Soft Seating",
    shortDescription: "Comfortable lounge and breakout furniture for relaxation zones.",
    fullDescription: "Breakout Furniture creates inviting spaces for relaxation, informal meetings, and creative thinking. Our range includes modular sofas, armchairs, coffee tables, and poufs that can be configured to suit any breakout area.",
    specifications: [
      { label: "Types", value: "Sofas, armchairs, ottomans, benches" },
      { label: "Modular Sofa Unit", value: "900mm x 900mm x 450mm (seat)" },
      { label: "Upholstery", value: "Commercial-grade fabric or vinyl" },
      { label: "Foam", value: "High-resilience molded foam" },
      { label: "Frame", value: "Kiln-dried hardwood or steel" },
      { label: "Legs", value: "Metal or wood options" },
      { label: "Fire Rating", value: "CAL 117 compliant" },
      { label: "Warranty", value: "5 years structural" },
    ],
    features: [
      "Modular configuration options",
      "Commercial-grade durability",
      "Stain-resistant fabrics",
      "High-resilience foam",
      "Easy reconfiguration",
      "Wide color palette",
    ],
    industries: ["Co-working", "Corporate HQ", "Start-ups"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ca7f8j23_Screenshot%202026-03-26%20165922.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ca7f8j23_Screenshot%202026-03-26%20165922.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/4wsohahw_imgi_62_bon-bon.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/h4lgf72a_Screenshot%202026-03-14%20142510.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/82c0ceta_Screenshot%202026-03-24%201622321.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/cl4fo0j4_Screenshot%202026-03-26%20170001.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/8rb5s38m_imgi_9_Bottega-Square.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/xwir16fo_imgi_10_Bottega-Tri.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/3v33zdfn_imgi_20_Dot-1.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/kylmlhgl_imgi_26_Dot-2.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/izn9irzi_imgi_32_Textured-Ottoman-Trio-on-White-Background.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/xdj57cr9_imgi_7_Jigsaw-1.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/derv7poy_imgi_7_TULISA_Booth2.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/jjowi32r_imgi_8_BFS-HNS-CCH.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/56ml7g0d_imgi_8_BFS-VRO-BTH.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/9pd9jsol_imgi_8_Jigsaw-2.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/6rqs5szg_imgi_3_BFS-STS-CH-2-1.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/n4jl18hj_imgi_4_BFS-STS-CH-3-1.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/41ug138j_imgi_5_BFS-ELC-CCH-2.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/o5r28ryr_imgi_6_BFS-ELC-CCH.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/701lrr6j_imgi_7_BFS-HNS-CCH-2.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/alubqjvs_Gemini_Generated_Image_lj32glj32glj32gl.png",
    ],
  },

  // BPO Products
  "acoustic-workstations": {
    name: "Acoustic Workstations",
    slug: "acoustic-workstations",
    category: "Workstations",
    shortDescription: "Sound-absorbing workstation systems for call centers and BPO operations.",
    fullDescription: "Acoustic Workstations are specifically designed for call center environments where noise management is critical. Featuring high partition panels with acoustic fabric and strategic sound absorption, these setups reduce noise pollution and improve call quality.",
    specifications: [
      { label: "Workstation Size", value: "1000mm x 600mm x 750mm" },
      { label: "Panel Height", value: "450mm to 600mm above desktop" },
      { label: "Acoustic Panel", value: "25mm thick, fabric wrapped" },
      { label: "NRC Rating", value: "0.75 - 0.85" },
      { label: "Material", value: "PLPB with melamine finish" },
      { label: "Cable Management", value: "Dedicated cable trays and ports" },
      { label: "Monitor Arms", value: "Compatible with standard mounts" },
      { label: "Layout Options", value: "Linear, cluster, 120° configurations" },
    ],
    features: [
      "High NRC acoustic panels",
      "Dense layout configurations",
      "24/7 operation durability",
      "Integrated cable management",
      "Monitor arm compatibility",
      "Quick installation design",
    ],
    industries: ["BPO", "IT & Technology"],
    image: "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/4s13te7v_Screenshot%202026-03-24%20162114-Picsart-AiImageEnhancer-Picsart-AiImageEnhancer.png",
    images: [
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/4s13te7v_Screenshot%202026-03-24%20162114-Picsart-AiImageEnhancer-Picsart-AiImageEnhancer.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/zs81btml_Gemini_Generated_Image_91lmf391lmf391lm.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/098b3yyv_Gemini_Generated_Image_598ys6598ys6598y.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/nmpruy7k_Gemini_Generated_Image_nr7024nr7024nr70.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/5oqpf2v3_Screenshot%202026-03-24%20163104-Picsart-AiImageEnhancer-Picsart-AiImageEnhancer-Picsart-AiImageEnhancer.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/jyagasso_Screenshot%202026-03-24%20163150-Picsart-AiImageEnhancer-Picsart-AiImageEnhancer-Picsart-AiImageEnhancer-Picsart-AiImageEnhancer.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/9f4r459y_Screenshot%202026-03-24%20162137-Picsart-AiImageEnhancer-Picsart-AiImageEnhancer.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/9u9wkrgn_Screenshot%202026-03-24%20163045-Picsart-AiImageEnhancer-Picsart-AiImageEnhancer-Picsart-AiImageEnhancer.png",
    ],
  },
  "24-7-chairs": {
    name: "24/7 Chairs",
    slug: "24-7-chairs",
    category: "Seating",
    shortDescription: "Heavy-duty ergonomic chairs rated for 24/7 continuous operation.",
    fullDescription: "24/7 Chairs are built for continuous use in shift-based operations. With reinforced components, enhanced ergonomics, and superior durability, these chairs withstand the demands of round-the-clock BPO and call center environments.",
    specifications: [
      { label: "Usage Rating", value: "24/7 continuous use certified" },
      { label: "Weight Capacity", value: "150kg" },
      { label: "Seat Width", value: "500mm" },
      { label: "Mechanism", value: "Heavy-duty synchronized tilt" },
      { label: "Foam", value: "High-density molded foam" },
      { label: "Upholstery", value: "Breathable mesh or fabric" },
      { label: "Base", value: "Heavy-duty nylon or steel" },
      { label: "Warranty", value: "5 years 24/7 operation" },
    ],
    features: [
      "24/7 operation certified",
      "Heavy-duty components",
      "Enhanced lumbar support",
      "Breathable upholstery options",
      "Reinforced mechanism",
      "Extended warranty coverage",
    ],
    industries: ["BPO", "Data Centers"],
    image: "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/ugc91cn9_imgi_80_Nemesis_highback.png",
    images: [
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/q2fhpp2z_imgi_15_Bruno-highback.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/fm3o545g_imgi_5_Alexis_1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/j4vjlien_imgi_13_Athena-midback.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/e191ma2f_imgi_14_Athena-family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/pm9q6yez_imgi_17_ceto_highback_03.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/ak4nqa6i_imgi_18_ceto_family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/7vh79z3w_imgi_26_Diana_01.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/0mmuqppm_imgi_23_Lucia_master.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/pdi5jq45_imgi_25_Dayna.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/8uqya98i_imgi_28_Eduardo.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/i9im8109_imgi_29_Eduardo-visitor.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/lhlz3e22_imgi_32_Logitech_embody_front.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/vhksr0av_imgi_34_Emma_Hb_front.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/1sztmw3s_imgi_35_Emma_Family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/9haqab9v_imgi_36_Eros_1-1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/88313jiv_imgi_37_Eros_2.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/fl5ulh96_imgi_38_Eve_1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/hab5mkg5_imgi_39_Eve_Family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/jid5kgxl_imgi_40_Freya_1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/ey0ca6t4_imgi_48_Hunter_1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/fc7833oe_imgi_50_Jane_01.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/co3s361u_imgi_53_Kendall_family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/dzmrcim8_imgi_54_Kori-Hb_Front.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/r0mxz5bp_imgi_56_Kylie_Highback.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/paftc42z_imgi_57_Kylie_Family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/4ffqnv84_imgi_61_Lauren_Family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/wvnydap4_imgi_66_Luca-highback.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/f0evq7pe_imgi_67_luca_family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/w7qeu1rf_imgi_68_Mateo-Highback.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/h059jqit_imgi_78_Monica_01.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/zsbls9ul_imgi_79_Monica_Family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/ugc91cn9_imgi_80_Nemesis_highback.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/uunp876a_imgi_81_Nemesis_family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/5zdxokjy_imgi_82_Noah_02.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/fv0liup8_imgi_83_Noah_Family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/mgvteuuq_imgi_84_Nova-grey.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/2dy4blmt_imgi_85_Nova_Family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/e4lzysw5_imgi_88_Phoenix_7-1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/bzpcybmo_imgi_90_Polly.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/v0j9n2rn_imgi_93_Roberto_1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/xez10p06_imgi_105_Sweetheart-Swivel-1_800px_new.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/3qcja90t_imgi_111_Titan.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/v1z9zn4o_imgi_112_vera.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/qhaj38gl_imgi_116_wyatt_family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/s38sg2dv_imgi_117_Zero_G_01.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/gmkqxgd8_imgi_141_Alexis_1-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/t0s6ql4v_imgi_186_Athena-midback-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/fxsomsmj_imgi_191_Athena-family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/p8xuglp1_imgi_196_Bruno-highback-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/fsdxt0w3_imgi_207_ceto_highback_03-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/ee36rj7w_imgi_212_ceto_family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/vz9o6ps3_imgi_235_Lucia_master-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/ljz5jvxe_imgi_249_Diana_01-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/i3rm9nek_imgi_259_Eduardo-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/c6nq1rjc_imgi_264_Eduardo-visitor-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/h3eacn1v_imgi_293_Emma_Hb_front-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/v7rby07q_imgi_298_Emma_Family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/1dd0lhfc_imgi_303_Eros_1-1-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/hdxqc5r2_imgi_308_Eros_2-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/0asbkl6v_imgi_313_Eve_1-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/0rb8hmsq_imgi_318_Eve_Family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/2y6irmrb_imgi_323_Freya_1-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/gkcwwxp8_imgi_362_Hunter_1-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/sx97ihbk_imgi_374_Jane_01-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/i4jr0px3_imgi_390_Kendall_family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/ls08aug9_imgi_395_Kori-Hb_Front-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/rxdymwjc_imgi_404_Kylie_Highback-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/6ys5jru8_imgi_409_Kylie_Family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/k60xbcq2_imgi_431_Lauren_Family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/z8qrtwo6_imgi_460_Luca-highback-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/lr1cttgn_imgi_465_luca_family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/73jo07et_imgi_470_Mateo-Highback-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/w3k0nlzu_imgi_519_Monica_01-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/63ikm83w_imgi_524_Monica_Family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/n7k62pu6_imgi_529_Nemesis_highback-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/imn0jiwe_imgi_534_Nemesis_family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/v4g6eae2_imgi_539_Noah_02-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/el61pxth_imgi_544_Noah_Family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/0j4966zp_imgi_549_Nova-grey-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/gb6rwmry_imgi_554_Nova_Family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/4oac7mdi_imgi_569_Phoenix_7-1-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/sp5fpef3_imgi_594_Roberto_1-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/si3dl17e_imgi_662_Sweetheart-Swivel-1_800px_new-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/vgb2k8n6_imgi_698_Titan-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/6hsjihbo_imgi_703_vera-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/ax5smjd3_imgi_723_wyatt_family-768x768.png",
    ],
  },
  "cable-trays": {
    name: "Cable Trays",
    slug: "cable-trays",
    category: "Accessories",
    shortDescription: "Under-desk and overhead cable management tray systems.",
    fullDescription: "Cable Trays provide organized cable routing for workstations and desks. Our systems include under-desk trays, overhead cable baskets, and vertical cable spines to maintain a clean, safe, and organized workspace.",
    specifications: [
      { label: "Types", value: "Under-desk, overhead, vertical spine" },
      { label: "Under-desk Tray", value: "800mm x 150mm x 100mm" },
      { label: "Material", value: "Powder-coated steel wire mesh" },
      { label: "Load Capacity", value: "15kg per meter" },
      { label: "Mounting", value: "Clip-on or screw-fixed" },
      { label: "Colors", value: "Black, white, silver" },
      { label: "Cable Entry", value: "Open design for easy access" },
      { label: "Accessories", value: "Cable clips, ties, grommets" },
    ],
    features: [
      "Multiple mounting options",
      "Easy cable access",
      "High load capacity",
      "Powder-coated finish",
      "Compatible with all desks",
      "Includes mounting hardware",
    ],
    industries: ["BPO", "IT & Technology", "All Industries"],
    image: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1280&q=70",
  },
  "partition-panels": {
    name: "Partition Panels",
    slug: "partition-panels",
    category: "Space Division",
    shortDescription: "Modular partition panels for workspace division and privacy.",
    fullDescription: "Partition Panels create defined workspaces and provide visual and acoustic separation. Available in fabric, glass, and whiteboard finishes, these panels can be configured to create private offices, meeting areas, or team zones.",
    specifications: [
      { label: "Heights", value: "1200mm, 1500mm, 1800mm, 2100mm" },
      { label: "Widths", value: "600mm, 800mm, 1000mm, 1200mm" },
      { label: "Thickness", value: "50mm (standard frames)" },
      { label: "Infill Options", value: "Fabric, glass, whiteboard, laminate" },
      { label: "Frame", value: "Aluminum or steel" },
      { label: "Acoustic NRC", value: "0.60 to 0.85 depending on infill" },
      { label: "Connections", value: "Straight, 90°, 120° angles" },
      { label: "Accessories", value: "Shelves, rails, power modules" },
    ],
    features: [
      "Multiple infill options",
      "Flexible configurations",
      "Acoustic performance",
      "Tool-less connection system",
      "Accessory rail compatibility",
      "Easy reconfiguration",
    ],
    industries: ["BPO", "Corporate HQ", "Co-working"],
    image: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1280&q=70",
  },
  "training-room-setup": {
    name: "Training Room Setup",
    slug: "training-room-setup",
    category: "Training Furniture",
    shortDescription: "Complete training room furniture packages for corporate learning.",
    fullDescription: "Training Room Setup packages include folding tables, stacking chairs, instructor stations, and AV furniture. Designed for flexibility, these solutions support various training configurations from classroom to workshop style.",
    specifications: [
      { label: "Table Types", value: "Folding, nesting, flip-top" },
      { label: "Table Size", value: "1500mm x 600mm (standard)" },
      { label: "Chair Types", value: "Stacking, tablet arm, training" },
      { label: "Instructor Station", value: "1800mm x 600mm with storage" },
      { label: "AV Cart", value: "Mobile projector/display stand" },
      { label: "Storage", value: "Table and chair storage carts" },
      { label: "Whiteboard", value: "Mobile or wall-mounted options" },
      { label: "Capacity", value: "Configured per room size" },
    ],
    features: [
      "Flexible reconfiguration",
      "Nesting/stacking storage",
      "Mobile AV solutions",
      "Integrated power options",
      "Multiple layout support",
      "Complete package solutions",
    ],
    industries: ["BPO", "Corporate HQ", "Public Sector"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ae48tr70_Screenshot%202026-03-25%20121014.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ae48tr70_Screenshot%202026-03-25%20121014.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/xkb3aq2d_Screenshot%202026-03-25%20121116.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/t9boh8t9_Screenshot%202026-03-25%20121417.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/555hkz0n_Screenshot%202026-03-25%20121653.png",
    ],
  },

  // Start-up Products
  "modular-desks": {
    name: "Modular Desks",
    slug: "modular-desks",
    category: "Workstations",
    shortDescription: "Budget-friendly modular desk systems for growing startups.",
    fullDescription: "Modular Desks offer cost-effective workspace solutions that grow with your startup. Easy to assemble, reconfigure, and expand, these desks provide professional workspaces without breaking the budget.",
    specifications: [
      { label: "Standard Size", value: "1200mm x 600mm x 750mm" },
      { label: "Material", value: "PLPB with laminate finish" },
      { label: "Leg Options", value: "Steel frame or shared beam" },
      { label: "Expansion", value: "Add-on units available" },
      { label: "Assembly", value: "Tool-less or minimal tools" },
      { label: "Cable Management", value: "Basic grommet and tray" },
      { label: "Colors", value: "White, maple, walnut finishes" },
      { label: "Lead Time", value: "Ready stock available" },
    ],
    features: [
      "Quick assembly design",
      "Expandable configurations",
      "Budget-friendly pricing",
      "Modern aesthetic options",
      "Ready stock availability",
      "Easy future expansion",
    ],
    industries: ["Start-ups", "Co-working", "Small Business"],
    image: "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/p0orltj7_64.png",
    images: [
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/p0orltj7_64.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/rugl8qeo_imgi_4_Rangecombo-MISSION-Transparent.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/0h39uvqs_Pic%2018.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/pp55q4cb_Gemini_Generated_Image_eke81weke81weke8.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/7q0ojmy5_Gemini_Generated_Image_kpx7a4kpx7a4kpx7.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/lcc8383v_imgi_3_Mission-Exec-Workstation-2000x1800-in-Sahara-Storm-Grey-Front-View-1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/kwm1e8di_imgi_9_ACTIVITY-System-S-Sahara-Loop-Leg-Workstation-with-Hinged-Door-Credenza-back-View-4.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/8425xbbn_imgi_5_Core-O-WhiteArden-Managers-Desk-Server-Kori.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/qm82r6v2_imgi_6_Core-O-CharcoalBrookhill-Managers-Desk-Server-1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/adnxeqpt_imgi_7_Core-U-BlackBrookhill-Managers-Desk-Server-1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/453bdosk_imgi_8_Core-U-CharcoalBrookhill-Managers-1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/o1dwz0du_imgi_93_Slope-L-shape-desk.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/qpjn68du_imgi_10_Rangecombo-ACTIVITY-Transparent.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/tq9sqp9f_imgi_40_Woodline-BlackBrookhill-2dr-3dw-Server-Front.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/1twuj30k_imgi_81_Gateway-New-Exec.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/ab1k9wsl_imgi_82_ACTIVITY-Sahara-Single-Desk-1500x750-System-no-storage-with-shadow-1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/lzkm2870_imgi_259_925-Single-Desk-2-Draw-Fitted.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/lotlmsk9_imgi_135_Milano-L-shape-front.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/8gc3g7ia_imgi_171_savona-chestnut.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/s03fusdu_imgi_213_Woodline-BlackBrookhill-2dr-3dw-Server-Front.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/jptwz5du_imgi_222_Woodline-BlackBrookhill-2dr-3dw-Back-nochair-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/oxhpe8gi_pic%2013.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/jcvvtnjo_pic%2015.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/wucd6b9i_pic%2016.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/j5krob4w_pic%2017.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/cu7he0wg_pic%2023.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/4unzzx5v_pic%2019.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/zn0efa3d_pic%2020.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/yxtvb86q_pic%2021.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/cv82ddx3_pic%2022.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/nrt9rwz3_pic%2028.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/bz2yato9_Pic%2024.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/zoht0h1e_pic%2025.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/wkp4x075_pic%2026.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/vol2ofzd_pic%2027.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/2m4r3px1_pic%2030.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/23efgd1y_pic%2029.png",
    ],
  },
  "collaborative-tables": {
    name: "Collaborative Tables",
    slug: "collaborative-tables",
    category: "Meeting Furniture",
    shortDescription: "Large shared tables for team collaboration and group work.",
    fullDescription: "Collaborative Tables bring teams together for brainstorming, project work, and casual meetings. These large-format tables feature integrated power, cable management, and durable surfaces for heavy daily use.",
    specifications: [
      { label: "Sizes", value: "2400mm, 3000mm, 3600mm lengths" },
      { label: "Width", value: "1200mm standard" },
      { label: "Material", value: "PLPB or plywood with laminate" },
      { label: "Power", value: "Pop-up or edge-mounted modules" },
      { label: "Seating Capacity", value: "6 to 12 persons" },
      { label: "Leg Style", value: "A-frame, loop, or pedestal" },
      { label: "Surface", value: "Writable laminate option" },
      { label: "Weight Capacity", value: "200kg distributed load" },
    ],
    features: [
      "Large collaborative surface",
      "Integrated power access",
      "Durable laminate finish",
      "Multiple size options",
      "Writable surface option",
      "Modern leg designs",
    ],
    industries: ["Start-ups", "IT & Technology", "Co-working"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/6j4wvxn5_Screenshot%202026-03-26%20133049.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/6j4wvxn5_Screenshot%202026-03-26%20133049.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/md9et6y8_Screenshot%202026-03-26%20132507.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/xp3jq7bq_Screenshot%202026-03-26%20132532.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/fpzk3057_Screenshot%202026-03-26%20132549.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/r4xo9for_Screenshot%202026-03-26%20132607.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/gimzln2l_Screenshot%202026-03-26%20133246.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/a0kohiuf_Screenshot%202026-03-26%20133114.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/61jxihf5_Screenshot%202026-03-26%20133130.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/mtlvzfho_Screenshot%202026-03-26%20133157.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/t7jctmr8_Screenshot%202026-03-26%20133223.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ce0s79ex_Screenshot%202026-03-26%20133331.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ucd7351l_Screenshot%202026-03-26%20133451.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/41oqnv5b_Screenshot%202026-03-26%20133602.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/1k1ci6xh_Screenshot%202026-03-26%20133734.png",
    ],
  },
  "bean-bags": {
    name: "Bean Bags",
    slug: "bean-bags",
    category: "Soft Seating",
    shortDescription: "Casual bean bag seating for relaxation and creative spaces.",
    fullDescription: "Bean Bags add a casual, creative vibe to startup and co-working spaces. Our commercial-grade bean bags feature durable fabrics, quality fills, and ergonomic shapes for comfortable lounging and informal meetings.",
    specifications: [
      { label: "Sizes", value: "Small, Medium, Large, Lounger" },
      { label: "Large Dimensions", value: "1000mm x 1000mm x 800mm" },
      { label: "Fill", value: "Virgin EPS beads" },
      { label: "Cover", value: "Commercial-grade fabric or leatherette" },
      { label: "Inner Liner", value: "Waterproof spill-resistant" },
      { label: "Zipper", value: "Heavy-duty YKK with child lock" },
      { label: "Fire Rating", value: "CAL 117 compliant" },
      { label: "Refill", value: "Refillable design" },
    ],
    features: [
      "Commercial-grade durability",
      "Waterproof inner liner",
      "Easy-clean outer cover",
      "Refillable design",
      "Fire-retardant options",
      "Multiple color options",
    ],
    industries: ["Start-ups", "Co-working", "Recreation Areas"],
    image: "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&w=1280&q=70",
  },
  "standing-desks": {
    name: "Standing Desks",
    slug: "standing-desks",
    category: "Ergonomic Furniture",
    shortDescription: "Fixed-height standing desks for active workstyles.",
    fullDescription: "Standing Desks promote an active work style at an accessible price point. These fixed-height desks are perfect for standing zones, quick-use areas, or users who prefer standing throughout the day.",
    specifications: [
      { label: "Desk Height", value: "1050mm (standing height)" },
      { label: "Work Surface", value: "1200mm x 600mm" },
      { label: "Material", value: "PLPB with laminate top" },
      { label: "Frame", value: "Powder-coated steel" },
      { label: "Footrest Bar", value: "Optional integrated footrest" },
      { label: "Mat", value: "Floor mat recommended for prolonged standing" },
      { label: "Cable Management", value: "Rear cable tray" },
      { label: "Adjustable Feet", value: "±15mm leveling" },
    ],
    features: [
      "Ergonomic standing height",
      "Stable steel frame",
      "Optional footrest bar",
      "Compatible with floor mats for standing comfort",
      "Cable management included",
      "Cost-effective solution",
    ],
    industries: ["Start-ups", "IT & Technology", "Co-working"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/z4dlj7cr_Screenshot%202026-03-14%20142405.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/z4dlj7cr_Screenshot%202026-03-14%20142405.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/31q3tjl7_Screenshot%202026-03-24%20160527.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/foxxaecm_46.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/oi6czfkz_65.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/x10582go_Screenshot%202026-03-24%20154157.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/9waeig3q_Screenshot%202026-03-24%20160542.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/32cc3czt_Screenshot%202026-03-24%20160559.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/tidlm7gb_Screenshot%202026-03-24%20160616.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ius26a19_Screenshot%202026-03-24%20160633.png",
    ],
  },
  "whiteboards": {
    name: "Whiteboards",
    slug: "whiteboards",
    category: "Accessories",
    shortDescription: "Mobile and wall-mounted whiteboards for collaboration.",
    fullDescription: "Whiteboards are essential for startup brainstorming and agile workflows. Our range includes mobile flip boards, wall-mounted panels, and glass boards in various sizes for team collaboration.",
    specifications: [
      { label: "Types", value: "Wall-mounted, mobile, glass" },
      { label: "Sizes", value: "900x600mm to 2400x1200mm" },
      { label: "Surface", value: "Melamine, porcelain, or tempered glass" },
      { label: "Frame", value: "Aluminum or frameless" },
      { label: "Mobile Stand", value: "Dual-sided flip option" },
      { label: "Magnetic", value: "Magnetic surface options" },
      { label: "Accessories", value: "Markers, erasers, magnets included" },
      { label: "Mounting", value: "Wall bracket or floor standing" },
    ],
    features: [
      "Multiple surface options",
      "Mobile and fixed versions",
      "Magnetic capability",
      "Dual-sided mobile boards",
      "Easy-clean surfaces",
      "Accessories included",
    ],
    industries: ["Start-ups", "IT & Technology", "All Industries"],
    image: "https://images.unsplash.com/photo-1531973576160-7125cd663d86?auto=format&fit=crop&w=1280&q=70",
  },

  // Public Sector Products
  "public-sector-desks": {
    name: "Public Sector Desks",
    slug: "public-sector-desks",
    category: "Public Sector Furniture",
    shortDescription: "Standard specification desks for public sector offices.",
    fullDescription: "Public Sector Desks are built to meet public sector specifications and procurement standards. These durable desks feature practical designs, fire-retardant materials, and standard sizing for uniform public sector office layouts.",
    specifications: [
      { label: "Standard Sizes", value: "1500x750mm, 1200x600mm" },
      { label: "Height", value: "750mm (standard)" },
      { label: "Material", value: "Commercial-grade PLPB" },
      { label: "Finish", value: "Scratch-resistant laminate" },
      { label: "Fire Rating", value: "Fire-retardant materials" },
      { label: "Drawers", value: "2 box + 1 file drawer" },
      { label: "Modesty Panel", value: "Full-height included" },
      { label: "Compliance", value: "Standard public sector specifications" },
    ],
    features: [
      "Public sector spec compliant",
      "Fire-retardant materials",
      "Durable construction",
      "Standard sizing",
      "Public sector compliant vendor",
      "Long-term warranty",
    ],
    industries: ["Public Sector"],
    image: "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/rugl8qeo_imgi_4_Rangecombo-MISSION-Transparent.png",
    images: [
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/rugl8qeo_imgi_4_Rangecombo-MISSION-Transparent.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/p0orltj7_64.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/0h39uvqs_Pic%2018.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/pp55q4cb_Gemini_Generated_Image_eke81weke81weke8.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/7q0ojmy5_Gemini_Generated_Image_kpx7a4kpx7a4kpx7.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/lcc8383v_imgi_3_Mission-Exec-Workstation-2000x1800-in-Sahara-Storm-Grey-Front-View-1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/kwm1e8di_imgi_9_ACTIVITY-System-S-Sahara-Loop-Leg-Workstation-with-Hinged-Door-Credenza-back-View-4.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/8425xbbn_imgi_5_Core-O-WhiteArden-Managers-Desk-Server-Kori.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/qm82r6v2_imgi_6_Core-O-CharcoalBrookhill-Managers-Desk-Server-1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/adnxeqpt_imgi_7_Core-U-BlackBrookhill-Managers-Desk-Server-1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/453bdosk_imgi_8_Core-U-CharcoalBrookhill-Managers-1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/o1dwz0du_imgi_93_Slope-L-shape-desk.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/qpjn68du_imgi_10_Rangecombo-ACTIVITY-Transparent.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/tq9sqp9f_imgi_40_Woodline-BlackBrookhill-2dr-3dw-Server-Front.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/1twuj30k_imgi_81_Gateway-New-Exec.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/ab1k9wsl_imgi_82_ACTIVITY-Sahara-Single-Desk-1500x750-System-no-storage-with-shadow-1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/lzkm2870_imgi_259_925-Single-Desk-2-Draw-Fitted.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/lotlmsk9_imgi_135_Milano-L-shape-front.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/8gc3g7ia_imgi_171_savona-chestnut.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/s03fusdu_imgi_213_Woodline-BlackBrookhill-2dr-3dw-Server-Front.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/jptwz5du_imgi_222_Woodline-BlackBrookhill-2dr-3dw-Back-nochair-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/oxhpe8gi_pic%2013.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/jcvvtnjo_pic%2015.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/wucd6b9i_pic%2016.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/j5krob4w_pic%2017.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/cu7he0wg_pic%2023.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/4unzzx5v_pic%2019.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/zn0efa3d_pic%2020.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/yxtvb86q_pic%2021.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/cv82ddx3_pic%2022.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/nrt9rwz3_pic%2028.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/bz2yato9_Pic%2024.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/zoht0h1e_pic%2025.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/wkp4x075_pic%2026.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/vol2ofzd_pic%2027.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/2m4r3px1_pic%2030.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/23efgd1y_pic%2029.png",
    ],
  },
  "filing-cabinets": {
    name: "Filing Cabinets",
    slug: "filing-cabinets",
    category: "Storage Solutions",
    shortDescription: "Heavy-duty filing cabinets for document storage.",
    fullDescription: "Filing Cabinets provide secure, organized storage for important documents. Built with heavy-gauge steel and featuring central locking, these cabinets meet public sector specifications for durability and security.",
    specifications: [
      { label: "Types", value: "Vertical 2/3/4 drawer, Lateral" },
      { label: "4-Drawer Vertical", value: "470mm x 620mm x 1330mm" },
      { label: "Material", value: "0.7mm CRCA steel" },
      { label: "Drawer Load", value: "45kg per drawer" },
      { label: "Lock", value: "Central locking mechanism" },
      { label: "Drawer Slides", value: "Full extension ball bearing" },
      { label: "Finish", value: "Powder-coated epoxy" },
      { label: "Fire Rating", value: "Fire-resistant option available" },
    ],
    features: [
      "Heavy-gauge steel construction",
      "Central locking system",
      "Anti-tilt mechanism",
      "Full extension drawers",
      "Fire-resistant options",
      "Label holders included",
    ],
    industries: ["Public Sector", "Banking", "Corporate HQ"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/se8uej32_Screenshot%202026-03-26%20121641.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/se8uej32_Screenshot%202026-03-26%20121641.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/pze2bvag_Screenshot%202026-03-26%20121745.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/901rnosj_imgi_7_Sahara-Storm-Grey-Melamine-Door-Bookcase-1600-4-Tier-1.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/u94iyd54_imgi_8_Sahara-Storm-Grey-Server-with-4-Door-1600x450x800h-1.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/2d8luyuc_imgi_23_Sahara-Storm-Grey-Wall-Unit-2-Door-4-Drawer-1.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/iqcdjcgh_imgi_25_Sahara-Storm-Grey-Open-Bookcase-1600-4-Tier.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/gc0ga9hf_imgi_3_BFS-LKR-RNG-2.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/c1tip16q_imgi_4_BFS-LKR-RNG-3.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/20zicj2q_imgi_5_BFS-LKR-RNG-4.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/5ymiu70o_imgi_6_BFS-LKR-RNG-7.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/8c4u5hds_imgi_41_BFS-LKR-RNG-8.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/cqf4doq9_imgi_37_BFS-LKR-RNG-7-600x600.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/iv5vemz7_imgi_50_Sahara-Storm-Grey-Server-with-4-Door-1600x450x800h-1-768x768.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/aufywm0o_Screenshot%202026-03-16%20140653.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/0ydhah5q_Screenshot%202026-03-25%20115341.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/0m2lpjwc_Screenshot%202026-03-25%20115428.png",
      "/media/filing-cabinets/filing-cabinet-01.jpg",
      "/media/filing-cabinets/filing-cabinet-02.jpg",
      "/media/filing-cabinets/filing-cabinet-03.jpg",
      "/media/filing-cabinets/filing-cabinet-12.jpg",
      "/media/filing-cabinets/filing-cabinet-13.jpg",
      "/media/filing-cabinets/filing-cabinet-16.jpg",
      "/media/filing-cabinets/filing-cabinet-17.jpg",
      "/media/filing-cabinets/filing-cabinet-20.jpg",
      "/media/filing-cabinets/filing-cabinet-21.jpg",
      "/media/filing-cabinets/filing-cabinet-24.jpg",
      "/media/filing-cabinets/filing-cabinet-25.jpg",
      "/media/filing-cabinets/filing-cabinet-26.jpg",
      "/media/filing-cabinets/filing-cabinet-27.jpg",
      "/media/filing-cabinets/filing-cabinet-28.jpg",
      "/media/filing-cabinets/filing-cabinet-29.jpg",
      "/media/filing-cabinets/filing-cabinet-30.jpg",
      "/media/filing-cabinets/filing-cabinet-31.jpg",
      "/media/filing-cabinets/filing-cabinet-32.jpg",
      "/media/filing-cabinets/filing-cabinet-33.jpg",
      "/media/filing-cabinets/filing-cabinet-38.jpg",
      "/media/filing-cabinets/filing-cabinet-39.jpg",
      "/media/filing-cabinets/filing-cabinet-41.jpg",
      "/media/filing-cabinets/filing-cabinet-43.jpg",
      "/media/filing-cabinets/filing-cabinet-45.jpg",
      "/media/filing-cabinets/filing-cabinet-46.jpg",
      "/media/filing-cabinets/filing-cabinet-47.jpg",
      "/media/filing-cabinets/filing-cabinet-48.jpg",
    ],
  },
  "visitor-chairs": {
    name: "Visitor Chairs",
    slug: "visitor-chairs",
    category: "Seating",
    shortDescription: "Durable visitor seating for waiting areas and meeting rooms.",
    fullDescription: "Visitor Chairs provide comfortable seating for guests, clients, and visitors. Available in cantilever and four-leg designs with various upholstery options, these chairs combine comfort with institutional durability.",
    specifications: [
      { label: "Types", value: "Cantilever, 4-leg, beam seating" },
      { label: "Seat Height", value: "450mm" },
      { label: "Seat Width", value: "480mm" },
      { label: "Frame", value: "Chrome or powder-coated steel" },
      { label: "Upholstery", value: "Fabric, vinyl, or mesh back" },
      { label: "Arms", value: "Fixed arms or armless options" },
      { label: "Weight Capacity", value: "120kg" },
      { label: "Stackable", value: "Up to 4 chairs (4-leg model)" },
    ],
    features: [
      "Durable steel frame",
      "Multiple upholstery options",
      "Stackable design available",
      "Chrome or painted finish",
      "Fixed or armless options",
      "High weight capacity",
    ],
    industries: ["Public Sector", "Corporate HQ", "All Industries"],
    image: "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/8uqya98i_imgi_28_Eduardo.png",
    images: [
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/q2fhpp2z_imgi_15_Bruno-highback.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/fm3o545g_imgi_5_Alexis_1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/j4vjlien_imgi_13_Athena-midback.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/e191ma2f_imgi_14_Athena-family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/pm9q6yez_imgi_17_ceto_highback_03.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/ak4nqa6i_imgi_18_ceto_family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/7vh79z3w_imgi_26_Diana_01.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/0mmuqppm_imgi_23_Lucia_master.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/pdi5jq45_imgi_25_Dayna.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/8uqya98i_imgi_28_Eduardo.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/i9im8109_imgi_29_Eduardo-visitor.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/lhlz3e22_imgi_32_Logitech_embody_front.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/vhksr0av_imgi_34_Emma_Hb_front.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/1sztmw3s_imgi_35_Emma_Family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/9haqab9v_imgi_36_Eros_1-1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/88313jiv_imgi_37_Eros_2.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/fl5ulh96_imgi_38_Eve_1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/hab5mkg5_imgi_39_Eve_Family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/jid5kgxl_imgi_40_Freya_1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/ey0ca6t4_imgi_48_Hunter_1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/fc7833oe_imgi_50_Jane_01.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/co3s361u_imgi_53_Kendall_family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/dzmrcim8_imgi_54_Kori-Hb_Front.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/r0mxz5bp_imgi_56_Kylie_Highback.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/paftc42z_imgi_57_Kylie_Family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/4ffqnv84_imgi_61_Lauren_Family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/wvnydap4_imgi_66_Luca-highback.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/f0evq7pe_imgi_67_luca_family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/w7qeu1rf_imgi_68_Mateo-Highback.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/h059jqit_imgi_78_Monica_01.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/zsbls9ul_imgi_79_Monica_Family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/ugc91cn9_imgi_80_Nemesis_highback.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/uunp876a_imgi_81_Nemesis_family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/5zdxokjy_imgi_82_Noah_02.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/fv0liup8_imgi_83_Noah_Family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/mgvteuuq_imgi_84_Nova-grey.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/2dy4blmt_imgi_85_Nova_Family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/e4lzysw5_imgi_88_Phoenix_7-1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/bzpcybmo_imgi_90_Polly.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/v0j9n2rn_imgi_93_Roberto_1.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/xez10p06_imgi_105_Sweetheart-Swivel-1_800px_new.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/3qcja90t_imgi_111_Titan.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/v1z9zn4o_imgi_112_vera.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/qhaj38gl_imgi_116_wyatt_family.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/s38sg2dv_imgi_117_Zero_G_01.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/gmkqxgd8_imgi_141_Alexis_1-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/t0s6ql4v_imgi_186_Athena-midback-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/fxsomsmj_imgi_191_Athena-family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/p8xuglp1_imgi_196_Bruno-highback-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/fsdxt0w3_imgi_207_ceto_highback_03-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/ee36rj7w_imgi_212_ceto_family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/vz9o6ps3_imgi_235_Lucia_master-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/ljz5jvxe_imgi_249_Diana_01-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/i3rm9nek_imgi_259_Eduardo-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/c6nq1rjc_imgi_264_Eduardo-visitor-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/h3eacn1v_imgi_293_Emma_Hb_front-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/v7rby07q_imgi_298_Emma_Family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/1dd0lhfc_imgi_303_Eros_1-1-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/hdxqc5r2_imgi_308_Eros_2-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/0asbkl6v_imgi_313_Eve_1-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/0rb8hmsq_imgi_318_Eve_Family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/2y6irmrb_imgi_323_Freya_1-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/gkcwwxp8_imgi_362_Hunter_1-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/sx97ihbk_imgi_374_Jane_01-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/i4jr0px3_imgi_390_Kendall_family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/ls08aug9_imgi_395_Kori-Hb_Front-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/rxdymwjc_imgi_404_Kylie_Highback-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/6ys5jru8_imgi_409_Kylie_Family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/k60xbcq2_imgi_431_Lauren_Family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/z8qrtwo6_imgi_460_Luca-highback-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/lr1cttgn_imgi_465_luca_family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/73jo07et_imgi_470_Mateo-Highback-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/w3k0nlzu_imgi_519_Monica_01-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/63ikm83w_imgi_524_Monica_Family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/n7k62pu6_imgi_529_Nemesis_highback-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/imn0jiwe_imgi_534_Nemesis_family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/v4g6eae2_imgi_539_Noah_02-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/el61pxth_imgi_544_Noah_Family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/0j4966zp_imgi_549_Nova-grey-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/gb6rwmry_imgi_554_Nova_Family-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/4oac7mdi_imgi_569_Phoenix_7-1-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/sp5fpef3_imgi_594_Roberto_1-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/si3dl17e_imgi_662_Sweetheart-Swivel-1_800px_new-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/vgb2k8n6_imgi_698_Titan-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/6hsjihbo_imgi_703_vera-768x768.png",
      "https://customer-assets.emergentagent.com/job_e267cc10-8abe-43da-88d1-389afddf0818/artifacts/ax5smjd3_imgi_723_wyatt_family-768x768.png",
    ],
  },
  "storage-units": {
    name: "Storage Units",
    slug: "storage-units",
    category: "Storage Solutions",
    shortDescription: "Versatile storage units for office organization.",
    fullDescription: "Storage Units provide flexible storage solutions for offices. From open shelving to enclosed cabinets, our range includes various configurations to organize supplies, equipment, and documents efficiently.",
    specifications: [
      { label: "Types", value: "Open shelving, closed cabinet, combination" },
      { label: "Heights", value: "900mm, 1200mm, 1800mm" },
      { label: "Width", value: "800mm, 1000mm" },
      { label: "Material", value: "PLPB or steel" },
      { label: "Shelves", value: "Adjustable shelf positions" },
      { label: "Doors", value: "Hinged, sliding, or roller shutter" },
      { label: "Lock", value: "Key lock standard" },
      { label: "Load Capacity", value: "50kg per shelf" },
    ],
    features: [
      "Multiple configuration options",
      "Adjustable shelving",
      "Lockable doors available",
      "Wood or steel options",
      "Modular expandability",
      "Professional appearance",
    ],
    industries: ["Public Sector", "Corporate HQ", "All Industries"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/2d8luyuc_imgi_23_Sahara-Storm-Grey-Wall-Unit-2-Door-4-Drawer-1.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/2d8luyuc_imgi_23_Sahara-Storm-Grey-Wall-Unit-2-Door-4-Drawer-1.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/901rnosj_imgi_7_Sahara-Storm-Grey-Melamine-Door-Bookcase-1600-4-Tier-1.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/u94iyd54_imgi_8_Sahara-Storm-Grey-Server-with-4-Door-1600x450x800h-1.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/iqcdjcgh_imgi_25_Sahara-Storm-Grey-Open-Bookcase-1600-4-Tier.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/gc0ga9hf_imgi_3_BFS-LKR-RNG-2.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/c1tip16q_imgi_4_BFS-LKR-RNG-3.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/20zicj2q_imgi_5_BFS-LKR-RNG-4.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/5ymiu70o_imgi_6_BFS-LKR-RNG-7.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/8c4u5hds_imgi_41_BFS-LKR-RNG-8.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/cqf4doq9_imgi_37_BFS-LKR-RNG-7-600x600.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/iv5vemz7_imgi_50_Sahara-Storm-Grey-Server-with-4-Door-1600x450x800h-1-768x768.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/se8uej32_Screenshot%202026-03-26%20121641.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/pze2bvag_Screenshot%202026-03-26%20121745.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/aufywm0o_Screenshot%202026-03-16%20140653.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/0ydhah5q_Screenshot%202026-03-25%20115341.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/0m2lpjwc_Screenshot%202026-03-25%20115428.png",
    ],
  },

  // Banking Products
  "teller-counters": {
    name: "Teller Counters",
    slug: "teller-counters",
    category: "Banking Furniture",
    shortDescription: "Secure teller workstations for bank branches.",
    fullDescription: "Teller Counters are specifically designed for banking operations with security features, cash handling areas, and customer-facing service points. These counters balance security with accessibility and professional appearance.",
    specifications: [
      { label: "Counter Height", value: "1050mm (customer side)" },
      { label: "Work Height", value: "750mm (teller side)" },
      { label: "Module Width", value: "1200mm per teller position" },
      { label: "Material", value: "Engineered wood with solid surface top" },
      { label: "Cash Drawer", value: "Integrated secure cash drawer" },
      { label: "Security", value: "Bullet-resistant glass options" },
      { label: "Cable Management", value: "Integrated for POS systems" },
      { label: "ADA Compliance", value: "Accessible height positions" },
    ],
    features: [
      "Secure cash handling areas",
      "Bullet-resistant options",
      "ADA compliant heights",
      "Integrated technology routing",
      "Durable transaction surfaces",
      "Professional banking aesthetic",
    ],
    industries: ["Banking"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/llma8rb8_imgi_12_IMG_0308.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/llma8rb8_imgi_12_IMG_0308.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/5hzgmshu_imgi_14_DSC023200.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/cft7fouu_imgi_52_POD-D-RENDER-06_26_2024-02_58-2048x1638.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/vytkq7fg_Screenshot%202026-03-25%20120614.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/6h393poq_Screenshot%202026-03-25%20120803.png",
    ],
  },
  "secure-cabinets": {
    name: "Secure Cabinets",
    slug: "secure-cabinets",
    category: "Banking Furniture",
    shortDescription: "High-security storage cabinets for sensitive documents.",
    fullDescription: "Secure Cabinets provide high-security storage for sensitive banking documents, cash, and valuables. Featuring reinforced construction, advanced locking mechanisms, and optional fire protection.",
    specifications: [
      { label: "Sizes", value: "Various configurations available" },
      { label: "Material", value: "Heavy-gauge steel (1.2mm+)" },
      { label: "Lock Types", value: "Key, digital, biometric options" },
      { label: "Fire Rating", value: "30-minute to 2-hour options" },
      { label: "Anchor Points", value: "Floor mounting bolts included" },
      { label: "Interior", value: "Adjustable shelves and drawers" },
      { label: "Finish", value: "Powder-coated or textured" },
      { label: "Certification", value: "Security rated per requirements" },
    ],
    features: [
      "Heavy-gauge steel construction",
      "Multiple lock options",
      "Fire protection available",
      "Floor anchoring capability",
      "Adjustable interior",
      "Security certifications",
    ],
    industries: ["Banking", "Corporate HQ"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/aufywm0o_Screenshot%202026-03-16%20140653.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/aufywm0o_Screenshot%202026-03-16%20140653.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/0ydhah5q_Screenshot%202026-03-25%20115341.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/0m2lpjwc_Screenshot%202026-03-25%20115428.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/901rnosj_imgi_7_Sahara-Storm-Grey-Melamine-Door-Bookcase-1600-4-Tier-1.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/u94iyd54_imgi_8_Sahara-Storm-Grey-Server-with-4-Door-1600x450x800h-1.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/2d8luyuc_imgi_23_Sahara-Storm-Grey-Wall-Unit-2-Door-4-Drawer-1.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/iqcdjcgh_imgi_25_Sahara-Storm-Grey-Open-Bookcase-1600-4-Tier.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/gc0ga9hf_imgi_3_BFS-LKR-RNG-2.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/c1tip16q_imgi_4_BFS-LKR-RNG-3.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/20zicj2q_imgi_5_BFS-LKR-RNG-4.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/5ymiu70o_imgi_6_BFS-LKR-RNG-7.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/8c4u5hds_imgi_41_BFS-LKR-RNG-8.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/cqf4doq9_imgi_37_BFS-LKR-RNG-7-600x600.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/iv5vemz7_imgi_50_Sahara-Storm-Grey-Server-with-4-Door-1600x450x800h-1-768x768.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/se8uej32_Screenshot%202026-03-26%20121641.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/pze2bvag_Screenshot%202026-03-26%20121745.png",
    ],
  },
  "customer-seating": {
    name: "Customer Seating",
    slug: "customer-seating",
    category: "Banking Furniture",
    shortDescription: "Comfortable waiting area seating for bank customers.",
    fullDescription: "Customer Seating creates comfortable waiting areas in bank branches. Available as individual chairs, beam seating, or modular configurations, these solutions maintain a professional banking environment.",
    specifications: [
      { label: "Types", value: "Individual, 2-seater, 3-seater beam" },
      { label: "Seat Height", value: "450mm" },
      { label: "Material", value: "Steel frame with upholstered seat" },
      { label: "Upholstery", value: "Commercial-grade fabric or vinyl" },
      { label: "Arms", value: "Shared or individual armrests" },
      { label: "Beam Mount", value: "Floor or wall-mounted options" },
      { label: "Table", value: "Optional integrated side tables" },
      { label: "Weight Capacity", value: "120kg per seat" },
    ],
    features: [
      "Professional banking aesthetic",
      "Durable commercial fabrics",
      "Multiple configuration options",
      "Integrated side tables available",
      "Easy clean surfaces",
      "Heavy-duty construction",
    ],
    industries: ["Banking", "Corporate HQ"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/axrjgzyi_Gemini_Generated_Image_d58n7vd58n7vd58n.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/axrjgzyi_Gemini_Generated_Image_d58n7vd58n7vd58n.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/cwz3buxd_Screenshot%202026-03-25%20111349.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/79hku51h_Screenshot%202026-03-25%20111435.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/3nmuq4tt_Screenshot%202026-03-25%20111711.png",
    ],
  },
  "safe-storage": {
    name: "Safe Storage",
    slug: "safe-storage",
    category: "Banking Furniture",
    shortDescription: "Deposit lockers and safe storage solutions.",
    fullDescription: "Safe Storage solutions include safe deposit lockers, document safes, and secure storage for banking and corporate environments. Multiple size configurations with advanced security features.",
    specifications: [
      { label: "Types", value: "Deposit lockers, document safes, vaults" },
      { label: "Locker Sizes", value: "Small, Medium, Large configurations" },
      { label: "Material", value: "Heavy steel with reinforced doors" },
      { label: "Lock", value: "Dual-key, digital, or combination" },
      { label: "Fire Rating", value: "Optional fire protection" },
      { label: "Installation", value: "Built-in or free-standing" },
      { label: "Access Control", value: "Audit trail options available" },
      { label: "Customization", value: "Size and configuration to order" },
    ],
    features: [
      "Multiple security levels",
      "Various size options",
      "Dual-key operation",
      "Fire protection available",
      "Audit trail capability",
      "Custom configurations",
    ],
    industries: ["Banking"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/0ydhah5q_Screenshot%202026-03-25%20115341.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/0ydhah5q_Screenshot%202026-03-25%20115341.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/0m2lpjwc_Screenshot%202026-03-25%20115428.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/aufywm0o_Screenshot%202026-03-16%20140653.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/901rnosj_imgi_7_Sahara-Storm-Grey-Melamine-Door-Bookcase-1600-4-Tier-1.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/u94iyd54_imgi_8_Sahara-Storm-Grey-Server-with-4-Door-1600x450x800h-1.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/2d8luyuc_imgi_23_Sahara-Storm-Grey-Wall-Unit-2-Door-4-Drawer-1.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/iqcdjcgh_imgi_25_Sahara-Storm-Grey-Open-Bookcase-1600-4-Tier.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/gc0ga9hf_imgi_3_BFS-LKR-RNG-2.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/c1tip16q_imgi_4_BFS-LKR-RNG-3.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/20zicj2q_imgi_5_BFS-LKR-RNG-4.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/5ymiu70o_imgi_6_BFS-LKR-RNG-7.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/8c4u5hds_imgi_41_BFS-LKR-RNG-8.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/cqf4doq9_imgi_37_BFS-LKR-RNG-7-600x600.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/iv5vemz7_imgi_50_Sahara-Storm-Grey-Server-with-4-Door-1600x450x800h-1-768x768.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/se8uej32_Screenshot%202026-03-26%20121641.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/pze2bvag_Screenshot%202026-03-26%20121745.png",
    ],
  },

  // Industrial Products
  "industrial-desks": {
    name: "Industrial Desks",
    slug: "industrial-desks",
    category: "Industrial Furniture",
    shortDescription: "Heavy-duty desks for industrial and factory environments.",
    fullDescription: "Industrial Desks are built to withstand demanding factory and industrial environments. Featuring reinforced construction, easy-clean surfaces, and resistance to dust, chemicals, and heavy use.",
    specifications: [
      { label: "Sizes", value: "1500x750mm, 1800x900mm" },
      { label: "Height", value: "750mm (standard)" },
      { label: "Frame", value: "Heavy-gauge steel tube" },
      { label: "Top Material", value: "High-pressure laminate or steel" },
      { label: "Surface", value: "Chemical and scratch resistant" },
      { label: "Drawers", value: "Optional heavy-duty drawers" },
      { label: "Weight Capacity", value: "200kg+ distributed load" },
      { label: "Powder Coating", value: "Industrial-grade finish" },
    ],
    features: [
      "Heavy-duty construction",
      "Chemical resistant surfaces",
      "Easy to clean and maintain",
      "High weight capacity",
      "Industrial-grade finish",
      "Optional drawer units",
    ],
    industries: ["Industrial"],
    image: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1280&q=70",
  },
  "heavy-duty-chairs": {
    name: "Heavy-duty Chairs",
    slug: "heavy-duty-chairs",
    category: "Industrial Furniture",
    shortDescription: "Robust seating for industrial and manufacturing environments.",
    fullDescription: "Heavy-duty Chairs are engineered for industrial environments with reinforced frames, easy-clean materials, and enhanced durability to withstand factory floor conditions.",
    specifications: [
      { label: "Weight Capacity", value: "180kg" },
      { label: "Frame", value: "Reinforced steel tube" },
      { label: "Seat Material", value: "Polyurethane or vinyl" },
      { label: "Height Adjustment", value: "Gas lift with extended range" },
      { label: "Base", value: "Heavy-duty nylon or steel" },
      { label: "Casters", value: "Industrial-grade options" },
      { label: "Foot Ring", value: "Optional for lab stools" },
      { label: "Clean Room", value: "ESD and clean room variants" },
    ],
    features: [
      "Extra high weight capacity",
      "Industrial-grade materials",
      "Easy-clean surfaces",
      "Extended height range",
      "ESD variants available",
      "Reinforced construction",
    ],
    industries: ["Industrial", "Data Centers"],
    image: "https://images.unsplash.com/photo-1580480055273-228ff5388ef8?auto=format&fit=crop&w=1280&q=70",
  },
  "storage-lockers": {
    name: "Storage Lockers",
    slug: "storage-lockers",
    category: "Industrial Furniture",
    shortDescription: "Industrial lockers for employee belongings and equipment.",
    fullDescription: "Storage Lockers provide secure storage for personal belongings and equipment in industrial settings. Heavy-duty construction with ventilation options and various compartment configurations.",
    specifications: [
      { label: "Configurations", value: "1-door to 6-door per unit" },
      { label: "Standard Dimensions", value: "1800mm H x 300mm W x 450mm D" },
      { label: "Material", value: "0.8mm CRCA steel" },
      { label: "Door Type", value: "Solid, louvered, or mesh" },
      { label: "Lock Options", value: "Padlock, cam lock, digital" },
      { label: "Ventilation", value: "Louvered or perforated options" },
      { label: "Finish", value: "Powder-coated, zinc options" },
      { label: "Accessories", value: "Hooks, shelves, mirror, bench" },
    ],
    features: [
      "Heavy-gauge steel construction",
      "Multiple lock options",
      "Ventilation for damp items",
      "Sloped top options",
      "Bench seat combinations",
      "Customizable configurations",
    ],
    industries: ["Industrial", "Co-working"],
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1280&q=70",
  },
  "supervisor-stations": {
    name: "Supervisor Stations",
    slug: "supervisor-stations",
    category: "Industrial Furniture",
    shortDescription: "Elevated workstations for factory floor supervision.",
    fullDescription: "Supervisor Stations provide elevated vantage points for factory floor oversight. These robust workstations include work surfaces, storage, and are designed for standing or stool-height operation.",
    specifications: [
      { label: "Platform Height", value: "150mm to 300mm options" },
      { label: "Work Surface", value: "1200mm x 600mm minimum" },
      { label: "Material", value: "Steel frame with laminate or steel top" },
      { label: "Storage", value: "Integrated shelving and drawers" },
      { label: "Railing", value: "Safety rail options" },
      { label: "Access", value: "Steps or ramp options" },
      { label: "Weight Capacity", value: "500kg platform load" },
      { label: "Mobility", value: "Fixed or mobile options" },
    ],
    features: [
      "Elevated overview position",
      "Integrated storage options",
      "Safety rail availability",
      "Heavy load capacity",
      "Mobile or fixed installation",
      "Durable industrial construction",
    ],
    industries: ["Industrial"],
    image: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1280&q=70",
  },
  "safety-cabinets": {
    name: "Safety Cabinets",
    slug: "safety-cabinets",
    category: "Industrial Furniture",
    shortDescription: "Certified safety cabinets for hazardous material storage.",
    fullDescription: "Safety Cabinets are designed for compliant storage of flammable, corrosive, and hazardous materials. Built to safety standards with proper ventilation, containment, and warning features.",
    specifications: [
      { label: "Types", value: "Flammable, corrosive, combination" },
      { label: "Capacity", value: "30, 45, 60, 90 gallon options" },
      { label: "Material", value: "18-gauge double-wall steel" },
      { label: "Door Type", value: "Manual or self-closing" },
      { label: "Ventilation", value: "Dual vents with caps" },
      { label: "Sump", value: "Built-in liquid containment" },
      { label: "Certification", value: "OSHA and NFPA compliant" },
      { label: "Color Coding", value: "Yellow (flammable), Blue (corrosive)" },
    ],
    features: [
      "Regulatory compliant",
      "Double-wall construction",
      "Built-in containment sump",
      "Self-closing door options",
      "Proper ventilation",
      "Clear warning labels",
    ],
    industries: ["Industrial"],
    image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1280&q=70",
  },

  // Office Partitions & Cabin Systems Products
  "glass-partitions": {
    name: "Single, Double & Grid Framed Partitions",
    slug: "glass-partitions",
    category: "Office Partitions & Cabin Systems",
    shortDescription: "Complete range of glass and framed partition systems — single glazed, double glazed, and grid framed — for every office space requirement.",
    fullDescription: "Our comprehensive partition range covers three distinct systems to suit any workspace need. Single Glazed Partitions deliver sleek, minimal framing with excellent transparency for open-plan offices. Double Glazed Partitions feature twin glass layers with an air gap for superior acoustic insulation (42-48 dB STC) — ideal for executive cabins and boardrooms. Grid Framed Partitions combine industrial-style steel or aluminum frames with glass or solid infills, creating a bold, contemporary aesthetic for creative offices and co-working spaces. All three systems maximize natural light, install faster than civil work, and integrate seamlessly with Ezeefit's modular furniture solutions.",
    specifications: [
      { label: "Single Glazed", value: "6mm or 8mm tempered glass, slim aluminum frame" },
      { label: "Double Glazed", value: "6mm + 12mm air gap + 6mm (24mm total)" },
      { label: "Grid Framed", value: "Powder-coated steel/aluminum, 300x300 or 400x400mm grid" },
      { label: "Acoustic Rating", value: "Single: 32-36 dB | Double: 42-48 dB STC" },
      { label: "Glass Types", value: "Clear, frosted, tinted, fluted, switchable privacy" },
      { label: "Height", value: "Up to 3300mm floor to ceiling" },
      { label: "Frame Finishes", value: "Anodized silver, black, white, bronze, custom RAL" },
      { label: "Door Options", value: "Framed swing, frameless pivot, sliding" },
    ],
    features: [
      "Single glazed for clean, minimal transparency",
      "Double glazed for superior acoustic privacy",
      "Grid framed for bold, industrial-modern aesthetics",
      "Maximizes natural light across office floors",
      "Multiple glass and infill options per system",
      "Factory-manufactured — no messy on-site carpentry",
    ],
    industries: ["Corporate HQ", "IT & Technology", "Banking", "Co-working"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/oha52fut_Screenshot%202026-03-26%20161944.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/oha52fut_Screenshot%202026-03-26%20161944.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/w8afz5mu_Double%20Glazed%20Partitions.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/znxwfa28_Gemini_Generated_Image_3zh5873zh5873zh5.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/8wfb68nb_Gemini_Generated_Image_d0rl66d0rl66d0rl.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/s04nyxp7_Gemini_Generated_Image_l3wlzhl3wlzhl3wl.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/auunqx4u_Grid%20Partitions.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/8iu0p4ha_Screenshot%202026-03-26%20161703.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/j3u9kftb_Gemini_Generated_Image_qgwbclqgwbclqgwb.png",
      "/media/glass-partitions/glass-partition-02.jpg",
      "/media/glass-partitions/glass-partition-04.jpg",
      "/media/glass-partitions/glass-partition-05.jpg",
      "/media/glass-partitions/glass-partition-06.jpg",
      "/media/glass-partitions/glass-partition-07.jpg",
      "/media/glass-partitions/glass-partition-08.jpg",
      "/media/glass-partitions/glass-partition-09.jpg",
      "/media/glass-partitions/glass-partition-10.jpg",
      "/media/glass-partitions/glass-partition-11.jpg",
      "/media/glass-partitions/glass-partition-12.jpg",
      "/media/glass-partitions/glass-partition-13.jpg",
      "/media/glass-partitions/glass-partition-14.jpg",
      "/media/glass-partitions/glass-partition-16.jpg",
      "/media/glass-partitions/glass-partition-17.jpg",
      "/media/glass-partitions/glass-partition-20.jpg",
      "/media/glass-partitions/glass-partition-21.jpg",
      "/media/glass-partitions/glass-partition-26.jpg",
      "/media/glass-partitions/glass-partition-27.jpg",
      "/media/glass-partitions/glass-partition-28.jpg",
      "/media/glass-partitions/glass-partition-29.jpg",
      "/media/glass-partitions/glass-partition-31.jpg",
      "/media/glass-partitions/glass-partition-32.jpg",
      "/media/glass-partitions/glass-partition-34.jpg",
    ],
  },
  "double-glazed-partitions": {
    name: "Single, Double & Grid Framed Partitions",
    slug: "double-glazed-partitions",
    category: "Office Partitions & Cabin Systems",
    shortDescription: "Complete range of glass and framed partition systems — single glazed, double glazed, and grid framed — for every office space requirement.",
    fullDescription: "Our comprehensive partition range covers three distinct systems to suit any workspace need. Single Glazed Partitions deliver sleek, minimal framing with excellent transparency for open-plan offices. Double Glazed Partitions feature twin glass layers with an air gap for superior acoustic insulation (42-48 dB STC) — ideal for executive cabins and boardrooms. Grid Framed Partitions combine industrial-style steel or aluminum frames with glass or solid infills, creating a bold, contemporary aesthetic for creative offices and co-working spaces. All three systems maximize natural light, install faster than civil work, and integrate seamlessly with Ezeefit's modular furniture solutions.",
    specifications: [
      { label: "Single Glazed", value: "6mm or 8mm tempered glass, slim aluminum frame" },
      { label: "Double Glazed", value: "6mm + 12mm air gap + 6mm (24mm total)" },
      { label: "Grid Framed", value: "Powder-coated steel/aluminum, 300x300 or 400x400mm grid" },
      { label: "Acoustic Rating", value: "Single: 32-36 dB | Double: 42-48 dB STC" },
      { label: "Glass Types", value: "Clear, frosted, tinted, fluted, switchable privacy" },
      { label: "Height", value: "Up to 3300mm floor to ceiling" },
      { label: "Frame Finishes", value: "Anodized silver, black, white, bronze, custom RAL" },
      { label: "Door Options", value: "Framed swing, frameless pivot, sliding" },
    ],
    features: [
      "Single glazed for clean, minimal transparency",
      "Double glazed for superior acoustic privacy",
      "Grid framed for bold, industrial-modern aesthetics",
      "Maximizes natural light across office floors",
      "Multiple glass and infill options per system",
      "Factory-manufactured — no messy on-site carpentry",
    ],
    industries: ["Corporate HQ", "Banking", "IT & Technology", "Public Sector"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/j3u9kftb_Gemini_Generated_Image_qgwbclqgwbclqgwb.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/j3u9kftb_Gemini_Generated_Image_qgwbclqgwbclqgwb.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/w8afz5mu_Double%20Glazed%20Partitions.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/znxwfa28_Gemini_Generated_Image_3zh5873zh5873zh5.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/8wfb68nb_Gemini_Generated_Image_d0rl66d0rl66d0rl.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/s04nyxp7_Gemini_Generated_Image_l3wlzhl3wlzhl3wl.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/auunqx4u_Grid%20Partitions.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/8iu0p4ha_Screenshot%202026-03-26%20161703.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/oha52fut_Screenshot%202026-03-26%20161944.png",
    ],
  },
  "grid-framed-partitions": {
    name: "Grid Framed Partitions",
    slug: "grid-framed-partitions",
    category: "Office Partitions & Cabin Systems",
    shortDescription: "Modern grid-style framed partitions for contemporary office aesthetics.",
    fullDescription: "Grid Framed Partitions combine industrial aesthetics with modern office design. The distinctive grid pattern creates visual interest while defining spaces. Popular in creative offices, co-working spaces, and companies seeking a contemporary, design-forward look.",
    specifications: [
      { label: "Frame Material", value: "Powder-coated steel or aluminum" },
      { label: "Grid Sizes", value: "300x300mm, 400x400mm, custom" },
      { label: "Frame Finish", value: "Black, white, bronze, custom RAL" },
      { label: "Infill Options", value: "Clear glass, fluted glass, mesh, solid" },
      { label: "Height", value: "Up to 3000mm" },
      { label: "Panel Width", value: "600mm to 1200mm modules" },
      { label: "Glass Thickness", value: "6mm or 8mm tempered" },
      { label: "Door Integration", value: "Matching framed doors available" },
    ],
    features: [
      "Contemporary industrial aesthetic",
      "Multiple frame finish options",
      "Modular panel system",
      "Various infill choices",
      "Design-forward appearance",
      "Easy to reconfigure",
    ],
    industries: ["Co-working", "Start-ups", "IT & Technology", "Creative Offices"],
    image: "https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=1280&q=70",
  },
  "cubicle-systems": {
    name: "Cubicle Systems",
    slug: "cubicle-systems",
    category: "Office Partitions & Cabin Systems",
    shortDescription: "Complete cubicle workstation systems with integrated partitions.",
    fullDescription: "Cubicle Systems combine workspace and privacy in efficient modular units. Our systems include partition panels, work surfaces, storage, and accessories to create defined personal workspaces within open-plan offices.",
    specifications: [
      { label: "Cubicle Sizes", value: "6x6ft, 8x8ft, 8x10ft, custom" },
      { label: "Panel Heights", value: "1200mm, 1500mm, 1800mm" },
      { label: "Panel Types", value: "Fabric, glass, laminate, combo" },
      { label: "Work Surface", value: "L-shaped, U-shaped, straight" },
      { label: "Storage", value: "Overhead bins, pedestals, shelves" },
      { label: "Power", value: "Integrated electrical raceways" },
      { label: "Acoustic Rating", value: "NRC 0.65-0.85" },
      { label: "Connectivity", value: "Pre-wired for power and data" },
    ],
    features: [
      "Complete workspace solution",
      "Integrated storage options",
      "Multiple panel material choices",
      "Built-in power distribution",
      "Expandable configurations",
      "Easy reconfiguration",
    ],
    industries: ["BPO", "Public Sector", "Corporate HQ", "IT & Technology"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/90bms87p_ChatGPT%20Image%20Mar%2026%2C%202026%2C%2003_33_52%20PM.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/90bms87p_ChatGPT%20Image%20Mar%2026%2C%202026%2C%2003_33_52%20PM.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/0roc97g4_ChatGPT%20Image%20Mar%2026%2C%202026%2C%2003_34_27%20PM.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/jpv05auv_Cubicle%20Systems.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ask9qya1_Gemini_Generated_Image_10r2xs10r2xs10r2.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/mia5q260_Gemini_Generated_Image_bwqzkcbwqzkcbwqz.png",
    ],
  },
  "modular-walls": {
    name: "Modular Walls",
    slug: "modular-walls",
    category: "Office Partitions & Cabin Systems",
    shortDescription: "Demountable wall systems for creating rooms without construction.",
    fullDescription: "Modular Walls are full-height demountable partition systems that create enclosed rooms, offices, and meeting spaces without permanent construction. Relocatable and reusable, they offer the privacy of walls with the flexibility of modular furniture.",
    specifications: [
      { label: "Height", value: "Floor to ceiling (up to 3600mm)" },
      { label: "Thickness", value: "80mm, 100mm, 120mm systems" },
      { label: "Infill Options", value: "Solid, glass, combination" },
      { label: "Acoustic Rating", value: "42-52 dB STC" },
      { label: "Door Integration", value: "Single, double, sliding doors" },
      { label: "Services", value: "Electrical, data, HVAC integration" },
      { label: "Finish", value: "Laminate, veneer, fabric, glass" },
      { label: "Relocation", value: "85% component reusability" },
    ],
    features: [
      "Full floor-to-ceiling enclosure",
      "Superior sound isolation",
      "Integrated services capability",
      "Demountable and relocatable",
      "No construction permits needed",
      "Sustainable - reusable components",
    ],
    industries: ["Corporate HQ", "Banking", "Public Sector"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/yryukeo5_Modular%20Walls%202.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/yryukeo5_Modular%20Walls%202.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/zap1rzjo_Modular%20Walls.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/wzdoocug_Screenshot%202026-03-26%20152457.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/csectm8j_Screenshot%202026-03-26%20154835.png",
    ],
  },
  "privacy-screens": {
    name: "Privacy Screens",
    slug: "privacy-screens",
    category: "Office Partitions & Cabin Systems",
    shortDescription: "Desktop and freestanding privacy screens for personal workspace definition.",
    fullDescription: "Privacy Screens provide personal space definition in open offices. From desk-mounted screens to freestanding dividers, these solutions offer visual privacy, reduce distractions, and help employees focus without full enclosure.",
    specifications: [
      { label: "Types", value: "Desk-mounted, clamp-on, freestanding" },
      { label: "Heights", value: "300mm, 450mm, 600mm desktop" },
      { label: "Materials", value: "Fabric, frosted acrylic, PET felt" },
      { label: "Mounting", value: "Clamp, brackets, or freestanding" },
      { label: "Acoustic Option", value: "PET felt NRC 0.40-0.60" },
      { label: "Desk Compatibility", value: "Universal mounting systems" },
      { label: "Thickness", value: "12mm-25mm depending on material" },
      { label: "Customization", value: "Colors, sizes, shapes available" },
    ],
    features: [
      "Quick desk-level privacy",
      "Multiple material options",
      "Easy installation",
      "Lightweight and portable",
      "Cost-effective solution",
      "Wide color range",
    ],
    industries: ["Co-working", "IT & Technology", "BPO", "Start-ups"],
    image: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/o1y9ibk5_Fabric%20Privacy%20Screen.png",
    images: [
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/o1y9ibk5_Fabric%20Privacy%20Screen.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/x930wjlb_imgi_3_BFS-SWS.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/znnx31rt_imgi_23_BFS-SAS-247x247.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/idu0kbne_imgi_35_BFS-SSS-600x600.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/y6vbrs9w_partition_0_1.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/ve4vlqwc_Screenshot%202026-03-24%20161438.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/wgnyeco6_partition_set2_0_1.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/vskczf4q_partition_set2_1_0.png",
      "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/jdck6fda_partition_set2_2_2.png",
    ],
  },
};

// All industries for the CTA section
const ALL_INDUSTRIES = [
  { name: "IT & Technology", slug: "it-technology" },
  { name: "Corporate HQ", slug: "corporate-hq" },
  { name: "Co-working Spaces", slug: "coworking" },
  { name: "BPO / Call Centers", slug: "bpo" },
  { name: "Start-ups", slug: "startups" },
  { name: "Banking & Finance", slug: "banking-finance" },
  { name: "Office Partitions & Cabins", slug: "office-partitions" },
];

// Industry name to slug mapping
const INDUSTRY_SLUG_MAP = {
  "IT & Technology": "it-technology",
  "Corporate HQ": "corporate-hq",
  "Co-working": "coworking",
  "BPO": "bpo",
  "Start-ups": "startups",
  "Banking": "banking-finance",
  "Office Partitions & Cabin Systems": "office-partitions",
};

// Solution slug to products mapping (for related products feature)
const SOLUTION_PRODUCTS = {
  "it-technology": ["Linear Workstations", "Height Adjustable Desks", "Phone Booths", "Meeting Pods", "Conference Tables"],
  "corporate-hq": ["Executive Desks", "Boardroom Tables", "Reception Counters", "Executive Chairs", "Filing Systems"],
  "coworking": ["Hot Desks", "Phone Booths", "Meeting Booths", "Locker Systems", "Breakout Furniture"],
  "bpo": ["Acoustic Workstations", "24/7 Chairs", "Training Room Setup"],
  "startups": ["Modular Desks", "Collaborative Tables", "Standing Desks"],
  "banking-finance": ["Teller Counters", "Secure Cabinets", "Customer Seating", "Executive Desks", "Safe Storage"],
  "office-partitions": ["Single, Double & Grid Framed Partitions", "Cubicle Systems", "Modular Wall Partitions", "Privacy Screens"],
};

// Helper function to get product slug from name
const getProductSlug = (productName) => {
  const slugOverrides = {
    "Single, Double & Grid Framed Partitions": "glass-partitions",
    "Modular Wall Partitions": "modular-walls",
  };
  if (slugOverrides[productName]) return slugOverrides[productName];
  return productName.toLowerCase()
    .replace(/\//g, '-')
    .replace(/&/g, 'and')
    .replace(/\s+/g, '-')
    .replace(/24\/7/g, '24-7');
};

// Atomic answer summaries for AI search optimization
const ATOMIC_ANSWERS = {
  "linear-workstations": "Modular linear workstations are factory-built desking systems arranged in a continuous line with shared cable spines and partition screens. Ezeefit manufactures linear workstations in 1200, 1350 and 1500 mm widths with integrated cable management, ISO 9001:2015 certification, 1–3 day installation and pricing starting around INR 6,000 per seat — built in Bhiwandi, Thane and delivered pan-India.",
  "corner-workstations": "Modular office corner workstations are L-shaped desking systems that sit inside a floor corner and give a single user roughly 40% more usable desktop area than a straight linear desk of the same footprint. Ezeefit manufactures corner workstations in 1500 × 1500 mm and 1800 × 1500 mm configurations, in 4-, 6-, 8- and 12-seater clusters, with 25 mm pre-laminated table-tops, powder-coated CRCA steel frames, integrated cable management, fabric / glass / laminate partition screens (300–900 mm) and optional mobile pedestals — ISO 9001:2015 certified, priced from INR 8,500 per seat, installed in 1–3 days in Mumbai, Navi Mumbai, Thane, Pune and pan-India from the Bhiwandi (Thane) factory.",
  "height-adjustable-desks": "Height adjustable modular desks (sit-stand desks) allow users to switch between seated and standing posture during the workday. Ezeefit electric sit-stand desks offer a 700–1150 mm height range, 120 kg load capacity, anti-collision sensors and memory presets — supplied in bulk for 50–1000+ seat corporate roll-outs from the Bhiwandi, Thane facility.",
  "phone-booths": "Acoustic office phone booths are freestanding, acoustically isolated rooms for private calls on the open floor. Ezeefit phone booths achieve a measured 30–35 dB ambient noise reduction with integrated mechanical ventilation, LED lighting, motion-sensor activation and 220 V power — no civil work, installed in 2–4 hours per unit.",
  "meeting-pods": "Acoustic meeting pods are freestanding, acoustically isolated rooms installed on the open office floor for 4–8 person meetings. Ezeefit meeting pods deliver a measured 30–35 dB ambient attenuation with integrated AV cable pass-throughs, mechanical ventilation, LED lighting and same-day installation — no civil construction required.",
  "conference-tables": "Conference tables are the centerpiece of boardroom design, impacting meeting productivity and client impressions. Ezeefit conference tables range from INR 15,000-1,20,000 with cable management, power integration, and premium laminate finishes.",
  "executive-desks": "Executive desks define the leadership workspace with premium finishes and integrated storage. Ezeefit executive desks feature modesty panels, wire management, soft-close mechanisms, and scratch-resistant laminate surfaces.",
  "executive-chairs": "Executive task chairs from Ezeefit feature high-back design, synchronised tilt, adjustable lumbar support, and Class 4 gas lifts. Built to ISO 9001:2015 standards. Range from INR 8,000–35,000 with bulk pricing for 50+ chair corporate orders, delivered from the Bhiwandi facility.",
  "modular-workstations": "Modular workstations save 40-60% installation time versus traditional carpentry with reconfigurable factory-built components. Ezeefit modular workstations start at INR 6,000 per seat with ISO 9001:2015 certification.",
  "glass-partitions": "Modular office cabin glass partitions divide floor space without civil construction. Ezeefit glass partitions are manufactured with 10–12 mm toughened safety glass and aluminium framing, available in single-glazed, double-glazed (35–42 dB Rw acoustic) and grid-framed configurations — customised to exact cabin dimensions, installed in 2–5 working days.",
  "acoustic-workstations": "Acoustic workstations reduce ambient noise by 25-35dB in open offices, directly improving focus and productivity for BPO and call center operations.",
  "visitor-chairs": "Visitor chairs create the first impression for clients and guests entering your office. Ezeefit visitor chairs range from INR 4,000-12,000 with stackable designs and premium upholstery options.",
  "filing-systems": "Systematic filing systems improve document retrieval speed by 50% with organized storage. Ezeefit filing systems include lateral files, pedestal units, and compact storage with anti-tilt mechanisms.",
  "filing-cabinets": "Customizable office storage systems for B2B procurement — Ezeefit manufactures filing cabinets, lateral storage and lockable pedestals in laminate, veneer and powder-coated steel. Custom widths, heights, drawer counts, finishes and lock types (cam, central, electronic keypad, biometric) are configured at the project quote stage and built at the Bhiwandi, Thane facility.",
  "locker-systems": "Employee locker systems support hot-desking and hybrid work models by providing secure personal storage. Ezeefit lockers feature powder-coated steel construction with multiple locking options.",
  "standing-desks": "Standing desks let teams switch between sitting and standing positions during the workday — ideal for creative teams and startup environments. Ezeefit standing desks feature manual and electric height adjustment options.",
};

export default function ProductDetail() {
  const { slug } = useParams();
  const location = useLocation();
  // Direct product lookup. If the slug doesn't exist (e.g. Speicher renamed a slug for SEO),
  // useSlugAlias checks the redirect table for any rule pointing to THIS URL and resolves the source slug.
  const directProduct = PRODUCTS_DATA[slug];
  const { aliasSlug, loading: aliasLoading } = useSlugAlias("/products", directProduct ? null : slug);
  const product = directProduct || (aliasSlug ? PRODUCTS_DATA[aliasSlug] : null);
  // Canonical URL: when serving via alias, the NEW URL is canonical (that's the whole point of the rename).
  const canonicalSlug = slug;

  // Speicher (SEO portal) overrides: meta_title, meta_description, canonical, og:*, schema_jsonld + disable_default_schema toggle.
  // The hook also applies canonical/meta_description so we don't need the standalone useCanonical/useMetaDescription calls below
  // when an override exists — but we still call them as fallback so behaviour is unchanged for pages without an override.
  const seoOverride = usePageSEOOverride(
    slug ? `/products/${slug}` : null,
    product
      ? {
          title: GEO_SEO_OVERRIDES[slug]?.title,
          description:
            GEO_SEO_OVERRIDES[slug]?.metaDescription
            || `${product.name} by Ezeefit — modular office furniture manufacturer in Mumbai. ${product.tagline || ''} View specifications, gallery & get quotes.`,
          canonical: `https://ezeefitofficesystems.in/products/${slug}`,
          ogImage: product.image,
        }
      : {},
  );

  // Fallback (no-op when seoOverride already set these via the hook above)
  useCanonical(slug ? `https://ezeefitofficesystems.in/products/${slug}` : null);
  useMetaDescription(
    product
      ? (seoOverride?.meta_description
          || GEO_SEO_OVERRIDES[slug]?.metaDescription
          || `${product.name} by Ezeefit — modular office furniture manufacturer in Mumbai. ${product.tagline || ''} View specifications, gallery & get quotes.`)
      : null
  );

  // Detect whether the user arrived from the Products listing page:
  //   1) React Router state (set on the <Link state={{ fromProducts: true }}> on /products)
  //   2) document.referrer fallback for hard-navigations, back-forward cache, and shared URLs
  const fromProductsState = !!location.state?.fromProducts;
  const fromProductsReferrer =
    typeof document !== "undefined"
    && typeof document.referrer === "string"
    && /\/products\/?(\?|#|$)/.test(document.referrer);
  const cameFromProducts = fromProductsState || fromProductsReferrer;

  // Back link priority:
  //   1) Referring industry (from Solutions deep-link — explicit state)
  //   2) Default to /products (safe fallback for the /products/* URL path — even
  //      when the user came from Google/direct, "Back to Products" always resolves).
  const referringIndustry = location.state?.fromIndustry;
  const backLink = referringIndustry && !cameFromProducts
    ? `/solutions/${referringIndustry}`
    : "/products";

  const backLabel = referringIndustry && !cameFromProducts
    ? (ALL_INDUSTRIES.find(i => i.slug === referringIndustry)?.name || 'Solutions')
    : "Products";

  // Image gallery state
  const [selectedImage, setSelectedImage] = useState(0);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const productImages = product?.images || [product?.image];

  // Generate product code
  const getProductCode = (index) => {
    const productCode = product?.slug
      ?.split('-')
      .map(word => word[0]?.toUpperCase() || '')
      .join('') || 'PR';
    return `${productCode}-${String(index + 1).padStart(3, '0')}`;
  };

  // SEO-optimized alt text generator
  const getImageAlt = (index, context) => {
    const code = getProductCode(index);
    const name = product?.name || 'Office Furniture';
    const category = product?.category || '';
    const industry = product?.industries?.[0] || '';
    const base = `${name}${category ? ` ${category}` : ''}`;
    const slugLower = slug || '';
    const productKeyword = slugLower.includes('workstation') ? 'modular office workstations Mumbai'
      : slugLower.includes('chair') || slugLower.includes('executive') || slugLower.includes('visitor') || slugLower.includes('24-7') ? 'ergonomic office chairs Mumbai'
      : slugLower.includes('partition') || slugLower.includes('glass') ? 'office partitions and cubicles Mumbai'
      : slugLower.includes('conference') || slugLower.includes('boardroom') ? 'conference table office furniture Mumbai'
      : slugLower.includes('phone') || slugLower.includes('meeting') ? 'modular office furniture Mumbai'
      : slugLower.includes('reception') ? 'reception furniture office Mumbai'
      : slugLower.includes('cabin') || slugLower.includes('executive-desk') ? 'executive office cabin furniture Mumbai'
      : 'modular office furniture Mumbai';
    switch (context) {
      case 'main':
        return `${base} - Model ${code} - ${productKeyword} by Ezeefit Office Systems`;
      case 'thumb':
        return `${name} variant ${code}${industry ? ` for ${industry}` : ''} - office furniture manufacturer Mumbai`;
      case 'modal-preview':
        return `${base} ${code} close-up view - ${productKeyword} - Ezeefit`;
      case 'modal-grid':
        return `${name} design ${code} - ${productKeyword} - Ezeefit`;
      default:
        return `${name} - ${code} - ${productKeyword} by Ezeefit Office Systems`;
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    setSelectedImage(0);
    setShowGalleryModal(false);
    if (product) {
      const seo = GEO_SEO_OVERRIDES[slug];
      document.title = seo?.title || `${product.name} - Modular Office Furniture Mumbai | Ezeefit Office Systems`;
      // Override <meta name="description"> imperatively too (Helmet's child-meta override is unreliable in this codebase)
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute(
          'content',
          seo?.metaDescription ||
            `${product.name} by Ezeefit — modular office furniture manufacturer in Mumbai. ${product.tagline || ''} View specifications, gallery & get quotes.`
        );
      }
    }
  }, [slug, product]);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setShowGalleryModal(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  if (!product) {
    // Still resolving an alias from the redirect table? Show a quiet loading state
    // instead of "Not Found" so renamed-slug URLs don't briefly 404 before resolving.
    if (aliasLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center pt-20">
          <div className="text-center text-[#64748B] text-sm">Loading…</div>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#0F172A] mb-4">Product Not Found</h2>
          <Button asChild>
            <Link to="/solutions">View All Solutions</Link>
          </Button>
        </div>
      </div>
    );
  }

  // Pre-compute all SEO/schema values BEFORE return so Helmet receives plain strings.
  // `seoOverride` from the hook (above) carries Speicher's portal edits. `geoSeo` is the hardcoded fallback.
  const geoSeo = GEO_SEO_OVERRIDES[slug];
  const pageTitle = seoOverride?.meta_title || geoSeo?.title || `${product.name} | Ezeefit Office Systems`;
  const pageDescription = seoOverride?.meta_description || geoSeo?.metaDescription || `${product.name} by Ezeefit — modular office furniture manufacturer in Mumbai. ${product.tagline || ''} View specifications, gallery & get quotes. Corporate office furniture supplier Mumbai.`;
  const canonicalUrl = seoOverride?.canonical || `https://ezeefitofficesystems.in/products/${slug}`;
  const productSchemaJson = JSON.stringify(buildProductSchema(product, slug));
  const faqSchemaObj = buildFaqSchema(slug);
  const faqSchemaJson = faqSchemaObj ? JSON.stringify(faqSchemaObj) : null;
  const speakableSchemaJson = JSON.stringify(buildSpeakableSchema(slug));
  const breadcrumbSchemaJson = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://ezeefitofficesystems.in" },
      { "@type": "ListItem", position: 2, name: "Products", item: "https://ezeefitofficesystems.in/products" },
      { "@type": "ListItem", position: 3, name: product.name, item: canonicalUrl },
    ],
  });

  return (
    <div className="pt-20" data-testid="product-detail-page">
      {/* Meta tags (title, description, canonical, og:title, og:description) are handled
          dynamically by usePageSEOOverride based on the current URL — so overrides configured
          in the SEO portal for renamed/aliased product URLs (e.g. /products/modular-linear-...)
          apply on the live page. Keeping og:type + og:image static as those don't need overrides. */}
      <Helmet>
        <meta property="og:type" content="product" />
        <meta property="og:image" content={product.image} />
      </Helmet>
      {/* Default JSON-LD schemas. Hidden when Speicher sets disable_default_schema=true in the portal. */}
      {!seoOverride?.disable_default_schema && (
        <>
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: breadcrumbSchemaJson }} />
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: productSchemaJson }} />
          {faqSchemaJson && (
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqSchemaJson }} />
          )}
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: speakableSchemaJson }} />
        </>
      )}
      {/* Breadcrumb & Back */}
      <section className="py-6 bg-[#F8FAFC] border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <Link
            to={backLink}
            className="inline-flex items-center gap-2 text-[#64748B] hover:text-[#F97316] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to {backLabel}
          </Link>
        </div>
      </section>

      {/* Atomic Answer Summary Box */}
      {ATOMIC_ANSWERS[slug] && (
        <section className="py-4 bg-[#FFF7ED] border-b border-[#FDBA74]" data-testid="atomic-answer-box">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <p className="text-[#0F172A] text-sm font-medium leading-relaxed">{ATOMIC_ANSWERS[slug]}</p>
          </div>
        </section>
      )}

      {/* GEO Glossary — educational E-E-A-T block for terminology disambiguation */}
      {seoOverride?.glossary && (
        <section className="py-10 bg-white border-b border-[#E2E8F0]" data-testid="geo-glossary-block">
          <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="border-l-4 border-[#F97316] pl-6 py-2">
              <h2 className="text-base font-semibold text-[#0F172A] mb-3 tracking-tight">{seoOverride.glossary.title}</h2>
              <p className="text-sm text-[#475569] leading-relaxed">{seoOverride.glossary.body}</p>
            </div>
          </div>
        </section>
      )}

      {/* Product Hero */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Product Image Gallery */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Main Image */}
              <div className="aspect-[4/3] bg-[#F8FAFC] border border-[#E2E8F0] overflow-hidden mb-4 flex items-center justify-center">
                <img src={productImages[selectedImage] || product.image}
                  alt={getImageAlt(selectedImage, 'main')}
                  width="800" height="600"
                  className="max-w-full max-h-full object-contain" loading="eager" fetchpriority="high" decoding="async" />
              </div>
              
              {/* Thumbnail Gallery - 3 thumbnails + "View More" tile = 4-col grid (all same size) */}
              {productImages.length > 1 && (
                <div className="grid grid-cols-4 gap-3 mb-4">
                  {productImages.slice(0, 3).map((img, index) => {
                      const imageCode = getProductCode(index);
                      
                      return (
                        <div key={index} className="flex flex-col">
                          <button
                            onClick={() => setSelectedImage(index)}
                            className={`aspect-square overflow-hidden border-2 transition-all bg-[#F8FAFC] flex items-center justify-center ${
                              selectedImage === index 
                                ? 'border-[#F97316]' 
                                : 'border-[#E2E8F0] hover:border-[#94A3B8]'
                            }`}
                          >
                            <img src={img}
                              alt={getImageAlt(index, 'thumb')}
                              className="max-w-full max-h-full object-contain" loading="lazy" decoding="async" />
                          </button>
                          <span className={`mt-1 text-center text-xs font-mono tracking-wide ${
                            selectedImage === index 
                              ? 'text-[#F97316] font-semibold' 
                              : 'text-[#64748B]'
                          }`}>
                            {imageCode}
                          </span>
                        </div>
                      );
                    })}

                  {/* Show More Button — rendered as 4th cell, matches thumbnail aspect-square */}
                  {productImages.length > 3 && (
                    <button
                      onClick={() => setShowGalleryModal(true)}
                      className="aspect-square flex flex-col items-center justify-center bg-[#0F172A] hover:bg-[#F97316] text-white transition-colors rounded-lg"
                    >
                      <Grid3X3 className="w-6 h-6 mb-1" />
                      <span className="text-xs font-semibold">View More</span>
                      <span className="text-xs opacity-80">Products</span>
                    </button>
                  )}
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {product.industries.map((industry) => (
                  <span
                    key={industry}
                    className="px-3 py-1 bg-[#F8FAFC] border border-[#E2E8F0] text-sm text-[#64748B]"
                  >
                    {industry}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* Product Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <span className="inline-block font-mono text-sm text-[#F97316] tracking-wider uppercase mb-2">
                {product.category}
              </span>
              <h1 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4">
                {product.name}
                {GEO_SEO_OVERRIDES[slug]?.h1Suffix && (
                  <span className="block text-base lg:text-lg font-medium text-[#475569] mt-2 tracking-normal">{GEO_SEO_OVERRIDES[slug].h1Suffix}</span>
                )}
              </h1>
              <p className="text-lg text-[#64748B] mb-8">
                {product.fullDescription}
              </p>

              {/* Key Features */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
                  <Layers className="w-5 h-5 text-[#F97316]" />
                  Key Features
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {product.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-[#F97316] shrink-0 mt-0.5" />
                      <span className="text-sm text-[#334155]">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <Button
                asChild
                className="bg-[#F97316] hover:bg-orange-600 text-white rounded-none px-8 py-6 font-semibold uppercase tracking-wider"
              >
                <Link to="/contact">
                  Request Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Specifications */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-8">
              <Ruler className="w-6 h-6 text-[#F97316]" />
              <h2 className="text-2xl font-bold text-[#0F172A]">
                Technical Specifications
              </h2>
            </div>

            <div className="bg-white border border-[#E2E8F0]">
              <table className="w-full">
                <tbody>
                  {product.specifications.map((spec, index) => (
                    <tr
                      key={index}
                      className={index % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}
                    >
                      <td className="px-6 py-4 font-medium text-[#0F172A] w-1/3">
                        {spec.label}
                      </td>
                      <td className="px-6 py-4 text-[#64748B]">{spec.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </section>

      {/* GEO-OPTIMISED FAQ — matches FAQ JSON-LD for Google rich results + AI citations */}
      {GEO_FAQS[slug] && (
        <section className="py-16 bg-[#F8FAFC] border-y border-[#E2E8F0]" data-testid="geo-faq-section">
          <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
            <div className="mb-10">
              <span className="text-xs font-mono tracking-widest uppercase text-[#F97316] mb-3 block">Frequently Asked Questions</span>
              <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] tracking-tight leading-tight">
                About {product.name}
              </h2>
            </div>
            <div className="space-y-4">
              {GEO_FAQS[slug].map((item, idx) => (
                <details
                  key={idx}
                  className="group bg-white border border-[#E2E8F0] open:border-[#F97316] transition-colors"
                  data-testid={`geo-faq-item-${idx}`}
                  {...(idx === 0 ? { open: true } : {})}
                >
                  <summary className="cursor-pointer list-none px-6 py-5 flex items-start justify-between gap-4">
                    <h3 className="text-base font-semibold text-[#0F172A] leading-snug">{item.q}</h3>
                    <span className="flex-shrink-0 w-6 h-6 border border-[#E2E8F0] flex items-center justify-center text-[#475569] group-open:bg-[#F97316] group-open:text-white group-open:border-[#F97316] transition-colors">
                      <span className="block group-open:rotate-45 transition-transform text-lg leading-none">+</span>
                    </span>
                  </summary>
                  <div className="px-6 pb-5 -mt-1 text-sm text-[#475569] leading-relaxed">
                    {item.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Related Products from Same Solution */}
      {(() => {
        const displayIndustry = referringIndustry || (product?.industries?.[0] ? (INDUSTRY_SLUG_MAP[product.industries[0]] || null) : null);
        const displayLabel = displayIndustry
          ? (ALL_INDUSTRIES.find(i => i.slug === displayIndustry)?.name || 'Solutions')
          : null;
        const displayLink = displayIndustry ? `/solutions/${displayIndustry}` : '/solutions';

        return displayIndustry && SOLUTION_PRODUCTS[displayIndustry] ? (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <Package className="w-6 h-6 text-[#F97316]" />
                  <h2 className="text-2xl font-bold text-[#0F172A]">
                    Products for This Solution
                  </h2>
                </div>
                <Link
                  to={displayLink}
                  className="inline-flex items-center gap-2 text-[#F97316] font-semibold hover:underline"
                >
                  View {displayLabel}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="flex flex-wrap gap-3">
                {SOLUTION_PRODUCTS[displayIndustry]
                  .filter(productName => getProductSlug(productName) !== slug)
                  .map((productName) => {
                    const productSlug = getProductSlug(productName);
                    return (
                      <Link
                        key={productName}
                        to={`/products/${productSlug}`}
                        state={{ fromIndustry: displayIndustry }}
                        className="px-5 py-3 bg-[#0F172A] text-white font-mono hover:bg-[#F97316] transition-colors"
                      >
                        {productName}
                      </Link>
                    );
                  })}
              </div>
            </motion.div>
          </div>
        </section>
        ) : null;
      })()}

      {/* Related Reading — bidirectional internal linking into the blog (SEO topic clusters) */}
      <RelatedReading
        keywords={[product.name, ...(product.industries || []), ...(product.tags || []), "modular office furniture"]}
        heading={`More on ${product.name}`}
        subheading="Articles from our knowledge library covering this product category."
        testId="related-reading-product"
      />

      {/* Industry Solutions CTA */}
      <section className="py-20 bg-[#0F172A] noise-overlay">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
              Explore More
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Industry-wise Product Solutions
            </h2>
            <p className="text-[#94A3B8] max-w-2xl mx-auto">
              Discover specialized office furniture solutions tailored for your industry
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {ALL_INDUSTRIES.map((industry, index) => (
              <motion.div
                key={industry.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={`/solutions/${industry.slug}`}
                  className="block p-6 bg-white/5 border border-white/10 hover:border-[#F97316] transition-colors group"
                >
                  <h3 className="text-white font-semibold group-hover:text-[#F97316] transition-colors">
                    {industry.name}
                  </h3>
                  <div className="mt-2 flex items-center gap-2 text-[#F97316] text-sm">
                    View Products
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <Shield className="w-12 h-12 text-[#F97316] mx-auto mb-6" />
          <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4">
            Need Custom Specifications?
          </h2>
          <p className="text-[#64748B] mb-8">
            We can customize this product to meet your specific requirements. 
            Contact our team to discuss your project needs.
          </p>
          <Button
            asChild
            className="bg-[#F97316] hover:bg-orange-600 text-white rounded-none px-8 py-6 font-semibold uppercase tracking-wider"
          >
            <Link to="/contact">
              Discuss Requirements
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Gallery Modal */}
      <AnimatePresence>
        {showGalleryModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
            onClick={() => setShowGalleryModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative bg-white w-full max-w-[79rem] max-h-[99vh] overflow-hidden rounded-lg flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Logo Header - Fixed */}
              <div className="bg-white border-b border-[#E2E8F0] px-6 py-3 flex items-center gap-3 shrink-0">
                <img src="https://customer-assets.emergentagent.com/job_office-expansion-pro/artifacts/6rfx1lr3_image.png" 
                  alt="Ezeefit Office Systems - Premium Office Furniture Manufacturer Mumbai" 
                  className="h-10 object-contain" loading="lazy" decoding="async" />
                <span className="text-[#0F172A] font-semibold">Ezeefit Office Systems</span>
              </div>

              {/* Modal Header - Fixed */}
              <div className="bg-[#0F172A] text-white px-6 py-4 flex items-center justify-between shrink-0">
                <div>
                  <h3 className="text-xl font-bold">{product.name}</h3>
                  <p className="text-sm text-[#94A3B8]">{productImages.length} Product Variants</p>
                </div>
                <button
                  onClick={() => setShowGalleryModal(false)}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Content Area - Split into Preview and Thumbnails */}
              <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
                {/* Preview Section - Fixed/Sticky on left */}
                <div className="lg:w-1/2 p-4 bg-[#F8FAFC] border-b lg:border-b-0 lg:border-r border-[#E2E8F0] shrink-0">
                  {selectedImage !== null && (
                    <div className="h-full flex flex-col">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-lg font-mono font-bold text-[#F97316]">
                          {getProductCode(selectedImage)}
                        </span>
                        <span className="text-sm text-[#64748B]">
                          {selectedImage + 1} of {productImages.length}
                        </span>
                      </div>
                      <div className="flex-1 flex items-center justify-center bg-white rounded border border-[#E2E8F0] min-h-[200px] lg:min-h-[300px]">
                        <img src={productImages[selectedImage]}
                          alt={getImageAlt(selectedImage, 'modal-preview')}
                          className="max-w-full max-h-full object-contain p-2" loading="lazy" decoding="async" />
                      </div>
                    </div>
                  )}
                </div>

                {/* Thumbnail Grid - Scrollable on right */}
                <div className="flex-1 overflow-y-auto p-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-3 gap-3">
                    {productImages.map((img, index) => {
                      const imageCode = getProductCode(index);
                      
                      return (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.01 }}
                          className="flex flex-col"
                        >
                          <button
                            onClick={() => setSelectedImage(index)}
                            className={`aspect-square overflow-hidden border-2 transition-all bg-white flex items-center justify-center hover:border-[#F97316] ${
                              selectedImage === index 
                                ? 'border-[#F97316] ring-2 ring-[#F97316] ring-offset-2' 
                                : 'border-[#E2E8F0]'
                            }`}
                          >
                            <LazyImage
                              src={img}
                              alt={getImageAlt(index, 'modal-grid')}
                              containerClassName="w-full h-full flex items-center justify-center"
                              className="max-w-full max-h-full object-contain"
                            />
                          </button>
                          <span className={`mt-1 text-center text-xs font-mono tracking-wide ${
                            selectedImage === index 
                              ? 'text-[#F97316] font-bold' 
                              : 'text-[#64748B]'
                          }`}>
                            {imageCode}
                          </span>
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Export products data for use elsewhere
export { PRODUCTS_DATA };
